import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import ScheduleSend from "@mui/icons-material/ScheduleSend";
import {
    FinanceCard,
    FinanceSectionHeader,
    FinanceKpiTile,
    FinanceHeroStat,
    FinanceTrendChart,
    SPACE,
    FinanceDetailsSection,
} from "../shared/FinanceUiKit";
import { getGrossBilledAmount, getVatAmount, hasVatIncluded } from "../shared/vatDisplay";
import { getEdmState } from "../shared/edmStatus";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
    Alert,
    Autocomplete,
    Avatar,
    ButtonBase,
    Card,
    Chip,
    Divider,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    LinearProgress,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
} from "@mui/material";

import {
    AccountBalance,
    AccountBalanceWallet,
    Add,
    ArrowForward,
    ArrowUpward,
    ArrowDownward,
    BusinessCenter,
    CalendarMonth,
    CheckCircle,
    CreditScore,
    CurrencyExchange,
    Download,
    DirectionsBoat,
    Edit,
    Flight,
    FileUpload,
    FilterAlt,
    Inventory2,
    LocalShipping,
    Payments,
    PictureAsPdf,
    ReceiptLong,
    Refresh,
    Savings,
    TrendingDown,
    TrendingUp,
    Visibility,
    WarningAmber,
    WorkspacePremium,
} from "@mui/icons-material";
import FinanceStatusChip from "../components/FinanceStatusChip";
import { formatPercent } from "../utils/formatter";
import {
    financeText,
    getFinanceLanguage,
    saveFinanceLanguage,
} from "finance-language";
import { appAlert } from "components/AppDialog";

const API_URL = process.env.REACT_APP_API_URL;

const COLORS = {
    navy: "#0B1F3A",
    navySoft: "#12345B",
    blue: "#175CD3",
    blueLight: "#EFF8FF",
    cyan: "#0BA5EC",
    green: "#027A48",
    greenLight: "#ECFDF3",
    red: "#B42318",
    redLight: "#FEF3F2",
    amber: "#B54708",
    amberLight: "#FFFAEB",
    purple: "#6941C6",
    purpleLight: "#F4F3FF",
    text: "#101828",
    subtext: "#667085",
    border: "#E4E7EC",
    muted: "#F2F4F7",
    page: "#F5F7FA",
    white: "#FFFFFF",
};

const cardSx = {
    borderRadius: "10px",
    background: COLORS.white,
    border: "1px solid rgba(15, 23, 42, 0.12)",
    boxShadow: "none",
    overflow: "hidden",
};

const getJobNo = (job) =>
    job?.jobNo ||
    job?.job_no ||
    job?.shipmentNo ||
    job?.shipment_no ||
    job?.invoiceNo ||
    job?.invoice_no ||
    "N/A";

const COLLECTION_RISK_OPTIONS = [
    { value: "ALL", label: "All Credit Risks" },
    { value: "normal", label: "Normal" },
    { value: "overdue", label: "Overdue" },
    { value: "doubtful", label: "Doubtful" },
    { value: "bad_debt", label: "Bad Debt" },
    { value: "written_off", label: "Written Off" },
];

const COLLECTION_RISK_STYLES = {
    normal: {
        label: "Normal",
        color: COLORS.green,
        background: COLORS.greenLight,
        border: "#ABEFC6",
    },
    overdue: {
        label: "Overdue",
        color: COLORS.amber,
        background: COLORS.amberLight,
        border: "#FEDF89",
    },
    doubtful: {
        label: "Doubtful",
        color: COLORS.purple,
        background: COLORS.purpleLight,
        border: "#D9D6FE",
    },
    bad_debt: {
        label: "Bad Debt",
        color: COLORS.red,
        background: COLORS.redLight,
        border: "#FECDCA",
    },
    written_off: {
        label: "Written Off",
        color: "#344054",
        background: "#F2F4F7",
        border: "#D0D5DD",
    },
};

const normalizeCollectionRisk = (value) => {
    const normalized = String(value || "normal")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_");

    const aliases = {
        watch: "overdue",
        high: "doubtful",
        high_risk: "doubtful",
        highrisk: "doubtful",
        legal: "bad_debt",
        write_off: "written_off",
        writeoff: "written_off",
    };

    const resolvedStatus = aliases[normalized] || normalized;

    return COLLECTION_RISK_STYLES[resolvedStatus] ? resolvedStatus : "normal";
};

const getCollectionRisk = (job) => {
    const risk =
        job?.collectionRisk ||
        job?.collection_risk ||
        job?.invoice?.collectionRisk ||
        job?.invoice?.collection_risk ||
        {};
    const status = normalizeCollectionRisk(
        risk?.status ||
        job?.riskStatus ||
        job?.risk_status ||
        job?.invoice?.riskStatus,
    );

    return {
        ...risk,
        status,
        reason: risk?.reason || "",
        notes: risk?.notes || "",
        provisionAmount: Number(
            risk?.provisionAmount ?? risk?.provision_amount ?? 0,
        ),
    };
};

const CollectionRiskChip = ({ job, onClick }) => {
    const risk = getCollectionRisk(job);
    const style = COLLECTION_RISK_STYLES[risk.status];

    return (
        <Tooltip title={risk.reason || financeText("Open collection risk details")} arrow>
            <Chip
                size="small"
                clickable={Boolean(onClick)}
                onClick={onClick}
                label={financeText(style.label)}
                sx={{
                    color: style.color,
                    background: style.background,
                    border: `1px solid ${style.border}`,
                    fontWeight: 800,
                }}
            />
        </Tooltip>
    );
};

const normalizeCurrencyCode = (currency) => {
    const code = String(currency || "EUR")
        .trim()
        .toUpperCase();
    if (code === "EURO") return "EUR";
    if (code === "TL") return "TRY";
    return code;
};

const getCustomerInvoices = (job) =>
    [job?.customerInvoices, job?.customer_invoices, job?.invoices].find(
        Array.isArray,
    ) || [];

const getCurrency = (job) => {
    const firstInvoice = getCustomerInvoices(job)[0];

    return normalizeCurrencyCode(
        job?.sellingRateCurrency ||
        job?.selling_rate_currency ||
        job?.currency ||
        job?.invoiceCurrency ||
        job?.invoice_currency ||
        job?.financialSummary?.sellingRateCurrency ||
        job?.financialSummary?.totalCurrency ||
        firstInvoice?.sellingRateCurrency ||
        firstInvoice?.selling_rate_currency ||
        firstInvoice?.currency ||
        firstInvoice?.invoiceCurrency ||
        "EUR",
    );
};

const getCostCurrency = (job) => {
    const items =
        [
            job?.invoiceItems,
            job?.invoice_items,
            job?.cargoDetails,
            job?.cargo_details,
        ].find(Array.isArray) || [];
    const itemWithCurrency = items.find(
        (item) => item?.costCurrency || item?.cost_currency,
    );
    const itemCurrency =
        itemWithCurrency?.costCurrency || itemWithCurrency?.cost_currency;

    return normalizeCurrencyCode(
        job?.costCurrency ||
        job?.cost_currency ||
        job?.totalCostCurrency ||
        job?.total_cost_currency ||
        itemCurrency ||
        job?.financialSummary?.costCurrency ||
        job?.financialSummary?.totalCurrency ||
        "EUR",
    );
};

const getGrossRevenue = (item) => {
    const customerInvoices = getCustomerInvoices(item);

    if (customerInvoices.length) {
        return customerInvoices.reduce(
            (sum, invoice) => sum + getGrossRevenue(invoice),
            0,
        );
    }

    return Number(
        item?.grossInvoiceSalesAmount ||
        item?.gross_invoice_sales_amount ||
        item?.originalAmount ||
        item?.original_amount ||
        item?.invoiceSalesAmount ||
        item?.salesAmount ||
        item?.customerInvoiceAmount ||
        item?.actualRevenueTotal ||
        item?.actual_revenue_total ||
        item?.totalAmount ||
        item?.amount ||
        0,
    );
};

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const firstDefinedNumber = (...values) => {
    const value = values.find(
        (item) => item !== undefined && item !== null && item !== "",
    );

    return toNumber(value);
};

const getBuyingRate = (job) => {
    return firstDefinedNumber(
        job?.buyingRate,
        job?.buying_rate,

        job?.financialSummary?.buyingRate,
        job?.financial_summary?.buying_rate,

        job?.freightCharges?.buyingRate,
        job?.freight_charges?.buying_rate,

        job?.mblFinancialSummary?.buyingRate,
        job?.omblFinancialSummary?.buyingRate,
        job?.amblFinancialSummary?.buyingRate,
    );
};

const getOperationalCost = (job) => {
    const financialSummary = job?.financialSummary || {};
    const freightCharges = job?.freightCharges || {};

    /*
   * Road MBL
   */
    const roadCost =
        firstDefinedNumber(financialSummary.shipmentCostTotal) +
        firstDefinedNumber(financialSummary.convertedLinehaulCost) +
        firstDefinedNumber(financialSummary.convertedHandlingCost);

    /*
   * Ocean OMBL
   */
    const oceanCost =
        firstDefinedNumber(financialSummary.pickupTotal) +
        firstDefinedNumber(financialSummary.othcTotal) +
        firstDefinedNumber(financialSummary.oceanFreightTotal) +
        firstDefinedNumber(financialSummary.dthcTotal) +
        firstDefinedNumber(financialSummary.handlingTotal) +
        firstDefinedNumber(financialSummary.deliveryTotal);

    /*
   * Air AMBL
   */
    const airCost =
        firstDefinedNumber(financialSummary.pickupTotal) +
        firstDefinedNumber(financialSummary.othcTotal) +
        firstDefinedNumber(financialSummary.airFreightTotal) +
        firstDefinedNumber(financialSummary.dthcTotal) +
        firstDefinedNumber(financialSummary.handlingTotal) +
        firstDefinedNumber(financialSummary.deliveryTotal);

    /*
   * freightCharges fallback
   */
    const freightChargeCost =
        firstDefinedNumber(freightCharges.pickupTotal) +
        firstDefinedNumber(freightCharges.othc?.total) +
        firstDefinedNumber(freightCharges.oceanFreight?.total) +
        firstDefinedNumber(freightCharges.airFreight?.total) +
        firstDefinedNumber(freightCharges.dthc?.total) +
        firstDefinedNumber(freightCharges.handling?.total) +
        firstDefinedNumber(freightCharges.deliveryTotal);

    if (roadCost > 0) return roadCost;
    if (oceanCost > 0) return oceanCost;
    if (airCost > 0) return airCost;
    if (freightChargeCost > 0) return freightChargeCost;

    return 0;
};

const getBaseCost = (job) => {
    const explicitCost = firstDefinedNumber(
        job?.actualCostTotal,
        job?.actual_cost_total,
        job?.totalCost,
        job?.total_cost,
        job?.expectedCostTotal,
        job?.expected_cost_total,
        job?.operationalCost,
        job?.operational_cost,
        job?.totalSupplierCost,
        job?.total_supplier_cost,
        job?.supplierCost,
        job?.supplier_cost,
        job?.financialSummary?.totalCost,
        job?.financial_summary?.total_cost,
    );

    const invoiceItemCost = Array.isArray(job?.invoiceItems)
        ? job.invoiceItems.reduce(
            (sum, item) =>
                sum +
                firstDefinedNumber(
                    item?.totalCost,
                    item?.total_cost,
                    item?.cost,
                    item?.unitCost,
                    item?.unit_cost,
                ),
            0,
        )
        : 0;

    if (explicitCost > 0) {
        return explicitCost;
    }

    if (invoiceItemCost > 0) return invoiceItemCost;

    return getOperationalCost(job);
};

const getCost = (job) => {
    const baseCost = getBaseCost(job);
    const buyingRate = getBuyingRate(job);

    const hasExplicitTotalCost = [
        job?.actualCostTotal,
        job?.actual_cost_total,
        job?.totalCost,
        job?.total_cost,
        job?.expectedCostTotal,
        job?.expected_cost_total,
        job?.totalSupplierCost,
        job?.total_supplier_cost,
        job?.financialSummary?.totalCost,
        job?.financial_summary?.total_cost,
    ].some((value) => value !== undefined && value !== null && value !== "");

    const hasInvoiceItemCost =
        Array.isArray(job?.invoiceItems) &&
        job.invoiceItems.some((item) =>
            [
                item?.totalCost,
                item?.total_cost,
                item?.cost,
                item?.unitCost,
                item?.unit_cost,
            ].some((value) => value !== undefined && value !== null && value !== ""),
        );

    const alreadyIncluded =
        job?.totalCostIncludesBuyingRate === true ||
        job?.total_cost_includes_buying_rate === true ||
        job?.financialSummary?.totalCostIncludesBuyingRate === true;

    // Explicit total fields and invoice-item totals are treated as complete costs.
    // Buying rate is added only when cost had to be reconstructed from
    // operational components and the backend has not marked it as included.
    if (alreadyIncluded || hasExplicitTotalCost || hasInvoiceItemCost) {
        return baseCost;
    }

    return baseCost + buyingRate;
};

const getPaidAmount = (job) => {
    const customerInvoices = getCustomerInvoices(job);

    if (customerInvoices.length) {
        return customerInvoices.reduce(
            (sum, invoice) => sum + getPaidAmount(invoice),
            0,
        );
    }

    return Number(
        job?.paidAmount ||
        job?.paid_amount ||
        job?.collectedAmount ||
        job?.collected_amount ||
        0,
    );
};

const getReceivable = (job) => {
    const hasPaidAmount = [
        job?.paidAmount,
        job?.paid_amount,
        job?.collectedAmount,
        job?.collected_amount,
    ].some((value) => value !== undefined && value !== null && value !== "");

    if (hasPaidAmount) {
        return Math.max(getBilledRevenue(job) - getPaidAmount(job), 0);
    }

    const explicitBalance = [
        job?.balanceAmount,
        job?.balance_amount,
        job?.outstandingAmount,
        job?.outstanding_amount,
    ].find((value) => value !== undefined && value !== null && value !== "");

    if (explicitBalance !== undefined) {
        return Math.max(
            toNumber(explicitBalance) - getReturnSummary(job).issuedAmount,
            0,
        );
    }

    return Math.max(getBilledRevenue(job), 0);
};

const getPayable = (job) =>
    Number(
        job?.openPayable ||
        job?.open_payable ||
        job?.netPayableAmount ||
        job?.net_payable_amount ||
        0,
    );

const getProfit = (job) => getRevenue(job) - getCost(job);

const getMargin = (job) => {
    const revenue = getRevenue(job);
    return revenue ? (getProfit(job) / revenue) * 100 : 0;
};

const getCustomerName = (job) =>
    job?.customerName || job?.customer_name || job?.customer || "N/A";

const getSupplierName = (job) =>
    job?.supplierName ||
    job?.supplier_name ||
    job?.serviceProviderName ||
    job?.service_provider_name ||
    job?.linehaulServiceProvider ||
    job?.supplier?.companyName ||
    job?.serviceProvider?.companyName ||
    "N/A";

const normalizeCountry = (value) =>
    String(value || "")
        .trim()
        .toLocaleUpperCase("tr-TR")
        .replace(/Ä°/g, "I")
        .replace(/Ãœ/g, "U")
        .replace(/Å/g, "S")
        .replace(/Ä/g, "G")
        .replace(/Ã–/g, "O")
        .replace(/Ã‡/g, "C");

const getPartyCountryValues = (job) => {
    const relatedParties = [
        job,
        job?.customer,
        job?.customerDetails,
        job?.customerInfo,
        job?.serviceProvider,
        job?.supplier,
        job?.serviceProviderDetails,
        job?.globalAgent,
        job?.selectedBookingAgent,
        job?.selectedOriginAgent,
        job?.selectedDestinationAgent,
        job?.selectedNotifyAgent,
        job?.bookingAgent,
        job?.originAgent,
        job?.destinationAgent,
        ...(Array.isArray(job?.globalAgents) ? job.globalAgents : []),
        ...(Array.isArray(job?.serviceProviders) ? job.serviceProviders : []),
    ].filter((item) => item && typeof item === "object");

    return relatedParties
        .flatMap((party) => [
            party?.customerCountry,
            party?.serviceProviderCountry,
            party?.globalAgentCountry,
            ...(Array.isArray(party?.servicesCountry) ? party.servicesCountry : []),
        ])
        .filter(Boolean);
};

const normalizeLookupKey = (value) => {
    if (!value) return "";

    if (typeof value === "object") {
        return normalizeLookupKey(
            value?._id ||
            value?.id ||
            value?.$oid ||
            value?.nameKey ||
            value?.name ||
            value?.companyName,
        );
    }

    return String(value)
        .trim()
        .toLocaleUpperCase("tr-TR")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^A-Z0-9]+/g, " ")
        .trim();
};

const getJobPartyLookupKeys = (job) =>
    [
        job?.customerId,
        job?.customer_id,
        job?.customer,
        job?.customerName,
        job?.customer_name,
        job?.serviceProviderId,
        job?.service_provider_id,
        job?.serviceProvider,
        job?.serviceProviderName,
        job?.service_provider_name,
        job?.supplierId,
        job?.supplier_id,
        job?.supplier,
        job?.supplierName,
        job?.supplier_name,
        job?.globalAgentId,
        job?.global_agent_id,
        job?.globalAgent,
        job?.globalAgentName,
        job?.global_agent_name,
        job?.selectedBookingAgent,
        job?.selectedOriginAgent,
        job?.selectedDestinationAgent,
        job?.selectedNotifyAgent,
    ]
        .map(normalizeLookupKey)
        .filter(Boolean);

const getDomesticInternationalScope = (job, partyCountryLookup = new Map()) => {
    const matchedMasterCountries = getJobPartyLookupKeys(job).flatMap(
        (key) => partyCountryLookup.get(key) || [],
    );

    const isDomestic = [
        ...getPartyCountryValues(job),
        ...matchedMasterCountries,
    ].some((country) =>
        ["TURKEY", "TURKIYE"].includes(normalizeCountry(country)),
    );

    return isDomestic ? "DOMESTIC" : "INTERNATIONAL";
};

const getDomesticInternationalLabel = (job, partyCountryLookup) =>
    getDomesticInternationalScope(job, partyCountryLookup) === "DOMESTIC"
        ? "Domestic"
        : "International";

const normalizeText = (value) =>
    String(value || "")
        .trim()
        .toUpperCase()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ");

const getEntityId = (value) => {
    if (!value) return "";

    if (typeof value === "object") {
        return getEntityId(value?._id || value?.id || value?.$oid);
    }

    return String(value).trim();
};

const getInvoiceNo = (job) =>
    String(
        job?.invoiceNo ||
        job?.invoice_no ||
        job?.customerInvoiceNo ||
        job?.customer_invoice_no ||
        "",
    ).trim();

const getReturnStatus = (returnInvoice) =>
    String(
        returnInvoice?.returnStatus || returnInvoice?.return_status || "Draft",
    ).trim();

const getOriginalInvoiceId = (returnInvoice) =>
    getEntityId(
        returnInvoice?.originalInvoiceId ||
        returnInvoice?.original_invoice_id ||
        returnInvoice?.originalInvoice?._id ||
        returnInvoice?.originalInvoice?.id,
    );

const getOriginalInvoiceNo = (returnInvoice) =>
    String(
        returnInvoice?.originalInvoiceNumber ||
        returnInvoice?.original_invoice_number ||
        returnInvoice?.originalInvoiceNo ||
        returnInvoice?.original_invoice_no ||
        returnInvoice?.originalInvoice?.invoiceNo ||
        returnInvoice?.originalInvoice?.invoice_no ||
        "",
    ).trim();

const getReturnInvoiceAmount = (returnInvoice) =>
    Math.abs(
        firstDefinedNumber(
            returnInvoice?.returnedAmount,
            returnInvoice?.returned_amount,
            returnInvoice?.returnAmount,
            returnInvoice?.return_amount,
            returnInvoice?.returnedTotalWithVat,
            returnInvoice?.returned_total_with_vat,
            returnInvoice?.totalWithVat,
            returnInvoice?.total_with_vat,
            returnInvoice?.totalAmount,
            returnInvoice?.total_amount,
            returnInvoice?.invoiceSalesAmount,
            returnInvoice?.amount,
        ),
    );

const getLinkedReturnInvoices = (job) =>
    [
        job?.returnInvoices,
        job?.return_invoices,
        job?.linkedReturnInvoices,
        job?.linked_return_invoices,
    ].find(Array.isArray) || [];

const getReturnDate = (returnInvoice) => {
    const raw =
        returnInvoice?.returnDate ||
        returnInvoice?.return_date ||
        returnInvoice?.invoiceDate ||
        returnInvoice?.invoice_date ||
        returnInvoice?.createdAt ||
        returnInvoice?.created_at ||
        returnInvoice?.updatedAt ||
        returnInvoice?.updated_at;
    const date = raw ? new Date(raw) : null;

    return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
};

const RETURN_SUMMARY_CACHE = new WeakMap();

const getReturnSummary = (job) => {
    if (!job || typeof job !== "object") {
        return {
            status: "No Return",
            issuedAmount: 0,
            reservedAmount: 0,
            count: 0,
            latestReturnInvoice: null,
        };
    }

    const cachedSummary = RETURN_SUMMARY_CACHE.get(job);
    if (cachedSummary) return cachedSummary;

    const allReturns = Array.from(
        new Map(
            [
                ...getLinkedReturnInvoices(job),
                ...getCustomerInvoices(job).flatMap(getLinkedReturnInvoices),
            ].map((returnInvoice, index) => [
                getEntityId(returnInvoice?._id || returnInvoice?.id) ||
                `return-${index}`,
                returnInvoice,
            ]),
        ).values(),
    );
    const activeReturns = allReturns.filter(
        (returnInvoice) =>
            normalizeText(getReturnStatus(returnInvoice)) !== "CANCELLED",
    );
    const issuedReturns = activeReturns.filter(
        (returnInvoice) =>
            normalizeText(getReturnStatus(returnInvoice)) === "ISSUED",
    );
    const latestActiveReturn =
        [...activeReturns].sort((a, b) => getReturnDate(b) - getReturnDate(a))[0] ||
        null;
    const latestReturnInvoice =
        [...allReturns].sort((a, b) => getReturnDate(b) - getReturnDate(a))[0] ||
        null;
    const calculatedIssuedAmount = issuedReturns.reduce(
        (sum, returnInvoice) => sum + getReturnInvoiceAmount(returnInvoice),
        0,
    );
    const calculatedReservedAmount = activeReturns.reduce(
        (sum, returnInvoice) => sum + getReturnInvoiceAmount(returnInvoice),
        0,
    );
    const issuedAmount = allReturns.length
        ? calculatedIssuedAmount
        : Math.max(
            firstDefinedNumber(
                job?.returnedAmount,
                job?.returned_amount,
                getCustomerInvoices(job).reduce(
                    (sum, invoice) =>
                        sum +
                        firstDefinedNumber(
                            invoice?.returnedAmount,
                            invoice?.returned_amount,
                        ),
                    0,
                ),
            ),
            getGrossRevenue(job) -
            firstDefinedNumber(
                job?.netAmount,
                job?.net_amount,
                job?.invoiceSalesAmount,
                job?.salesAmount,
            ),
            0,
        );
    const reservedAmount = allReturns.length
        ? calculatedReservedAmount
        : Math.max(
            firstDefinedNumber(
                job?.reservedReturnAmount,
                job?.reserved_return_amount,
                issuedAmount,
            ),
            issuedAmount,
        );
    const originalAmount = Math.abs(getGrossRevenue(job));
    const latestStatus = latestActiveReturn
        ? normalizeText(getReturnStatus(latestActiveReturn))
        : "";

    let status = "No Return";
    if (latestStatus === "DRAFT") status = "Return Draft";
    else if (latestStatus === "APPROVED") status = "Return Approved";
    else if (
        issuedAmount > 0 &&
        originalAmount > 0 &&
        issuedAmount >= originalAmount - 0.01
    ) {
        status = "Full Return";
    } else if (issuedAmount > 0) status = "Partial Return";
    else if (reservedAmount > 0) status = "Return Pending";
    else if (allReturns.length > 0) status = "Return Cancelled";

    const summary = {
        status,
        issuedAmount,
        reservedAmount,
        count: allReturns.length,
        latestReturnInvoice,
    };

    RETURN_SUMMARY_CACHE.set(job, summary);
    return summary;
};

const getRevenue = (job) =>
    Math.max(getGrossRevenue(job) - getReturnSummary(job).issuedAmount, 0);

// VAT-inclusive counterparts. getGrossRevenue/getRevenue stay ex-VAT because
// profit and margin are measured against them; these show what customers are
// actually billed (equal to the ex-VAT figures for invoices without VAT).
const getBilledGrossRevenue = (item) => {
    const customerInvoices = getCustomerInvoices(item);

    if (customerInvoices.length) {
        return customerInvoices.reduce(
            (sum, invoice) => sum + getBilledGrossRevenue(invoice),
            0,
        );
    }

    return hasVatIncluded(item)
        ? toNumber(item?.grossSellingRateInclVat ?? item?.sellingRateInclVat)
        : getGrossRevenue(item);
};

const getBilledRevenue = (item) =>
    Math.max(
        getBilledGrossRevenue(item) - getReturnSummary(item).issuedAmount,
        0,
    );

const getJobVatAmount = (item) => {
    const customerInvoices = getCustomerInvoices(item);

    if (customerInvoices.length) {
        return customerInvoices.reduce(
            (sum, invoice) => sum + getJobVatAmount(invoice),
            0,
        );
    }

    return hasVatIncluded(item) ? getVatAmount(item) : 0;
};

const RETURN_STATUS_STYLES = {
    "No Return": { color: "#475467", background: "#F2F4F7", border: "#D0D5DD" },
    "Return Draft": {
        color: "#B54708",
        background: "#FFFAEB",
        border: "#FEDF89",
    },
    "Return Approved": {
        color: "#175CD3",
        background: "#EFF8FF",
        border: "#B2DDFF",
    },
    "Partial Return": {
        color: "#6941C6",
        background: "#F4F3FF",
        border: "#D9D6FE",
    },
    "Full Return": { color: "#B42318", background: "#FEF3F2", border: "#FECDCA" },
    "Return Pending": {
        color: "#B54708",
        background: "#FFFAEB",
        border: "#FEDF89",
    },
    "Return Cancelled": {
        color: "#475467",
        background: "#F2F4F7",
        border: "#D0D5DD",
    },
};

const getInvoiceOrigin = (job) =>
    normalizeText(job?.invoiceOrigin || job?.invoice_origin || "tms") === "MANUAL"
        ? "manual"
        : "tms";

const getTransportMode = (job) => {
    if (getInvoiceOrigin(job) === "manual") {
        return "Manual";
    }

    const raw = normalizeText(
        job?.sourceType ||
        job?.source_type ||
        job?.transportMode ||
        job?.transport_mode ||
        job?.shipmentMode ||
        job?.shipment_mode ||
        job?.mode ||
        job?.tmsSource ||
        job?.tms_source ||
        job?.source ||
        job?.module ||
        job?.serviceType ||
        job?.service_type ||
        getJobNo(job),
    );

    if (
        raw.includes("OCEAN") ||
        raw.includes("SEA") ||
        raw.includes("OMBL") ||
        raw.includes("OHFL")
    ) {
        return "Ocean";
    }

    if (
        raw.includes("ROAD") ||
        raw.includes("LAND") ||
        raw.includes("TRUCK") ||
        raw.includes("RMBL") ||
        raw.includes("RHFL")
    ) {
        return "Road";
    }

    if (raw.includes("AIR") || raw.includes("AMBL") || raw.includes("AHFL")) {
        return "Air";
    }

    return "Other";
};

const getShipmentType = (job) => {
    const raw = normalizeText(
        job?.shipmentType ||
        job?.shipment_type ||
        job?.transportType ||
        job?.transport_type ||
        job?.oceanTransportType ||
        job?.roadTransportType ||
        job?.airTransportType ||
        job?.serviceType ||
        job?.service_type ||
        job?.service ||
        job?.loadType ||
        job?.load_type,
    );

    const knownTypes = [
        "FCL",
        "LCL",
        "FTL",
        "LTL",
        "EXPRESS",
        "STANDARD",
        "CHARTER",
        "RORO",
        "RO RO",
        "BREAK BULK",
        "BB",
        "INTERMODAL",
        "COURIER",
    ];

    const matched = knownTypes.find((type) => raw.includes(type));
    if (matched === "RO RO") return "RORO";
    if (matched === "BREAK BULK" || matched === "BB") return "Break Bulk";
    if (matched) return matched.charAt(0) + matched.slice(1).toLowerCase();

    return raw
        ? raw.replace(/\b\w/g, (char) => char.toUpperCase())
        : "Unspecified";
};

const getDateValue = (job) => {
    const raw =
        job?.invoiceDate ||
        job?.invoice_date ||
        job?.customerInvoiceDate ||
        job?.supplierInvoiceDate ||
        job?.transactionDate ||
        job?.postingDate ||
        job?.createdAt ||
        job?.created_at ||
        job?.updatedAt ||
        job?.updated_at;

    const date = raw ? new Date(raw) : null;
    return date && !Number.isNaN(date.getTime()) ? date : null;
};

const formatCompact = (value, currency = "EUR") =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(Number(value || 0));

const formatNumber = (value) =>
    new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
        Number(value || 0),
    );

const formatMoney = (value, currency = "EUR") =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(value || 0));

const convertCurrency = ({ amount, fromCurrency, toCurrency, rates }) => {
    const numericAmount = Number(amount || 0);
    const from = String(fromCurrency || "EUR").toUpperCase();
    const to = String(toCurrency || "EUR").toUpperCase();

    if (from === to) return numericAmount;
    if (!rates?.[from] || !rates?.[to]) return numericAmount;

    const amountInBase = numericAmount / Number(rates[from]);
    return amountInBase * Number(rates[to]);
};

const getMonthKey = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const getMonthLabel = (date) =>
    new Intl.DateTimeFormat("en-US", { month: "short" }).format(date);

const buildMonthlySeries = (jobs, months = 6) => {
    const now = new Date();
    const base = Array.from({ length: months }, (_, index) => {
        const date = new Date(
            now.getFullYear(),
            now.getMonth() - (months - 1 - index),
            1,
        );
        return {
            key: getMonthKey(date),
            label: getMonthLabel(date),
            revenue: 0,
            cost: 0,
            profit: 0,
            collection: 0,
        };
    });

    const indexMap = new Map(base.map((item, index) => [item.key, index]));

    jobs.forEach((job) => {
        const date = getDateValue(job);
        if (!date) return;

        const index = indexMap.get(getMonthKey(date));
        if (index === undefined) return;

        base[index].revenue += getRevenue(job);
        base[index].cost += getCost(job);
        base[index].profit += getProfit(job);
        base[index].collection += getPaidAmount(job);
    });

    const hasData = base.some(
        (item) => item.revenue || item.cost || item.collection || item.profit,
    );

    if (!hasData && jobs.length) {
        const totalRevenue = jobs.reduce((sum, item) => sum + getRevenue(item), 0);
        const totalCost = jobs.reduce((sum, item) => sum + getCost(item), 0);
        const totalCollection = jobs.reduce(
            (sum, item) => sum + getPaidAmount(item),
            0,
        );

        return base.map((item, index) => {
            const factor = [0.72, 0.81, 0.76, 0.91, 0.94, 1][index] || 1;
            const revenue = (totalRevenue / months) * factor;
            const cost = (totalCost / months) * Math.max(factor - 0.06, 0.55);
            return {
                ...item,
                revenue,
                cost,
                profit: revenue - cost,
                collection: (totalCollection / months) * Math.max(factor - 0.03, 0.55),
            };
        });
    }

    return base;
};

const SectionHeader = ({ title, subtitle, action }) => (
    <MDBox
        display="flex"
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        flexDirection={{ xs: "column", md: "row" }}
        gap={1.5}
        mb={2.5}
    >
        <MDBox>
            <MDTypography variant="h6" fontWeight="bold" sx={{ color: COLORS.text }}>
                {title}
            </MDTypography>
            {subtitle && (
                <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                    {subtitle}
                </MDTypography>
            )}
        </MDBox>
        {action}
    </MDBox>
);

const DeltaBadge = ({ value = 0, label = "vs previous period" }) => {
    const positive = Number(value) >= 0;

    return (
        <MDBox display="flex" alignItems="center" gap={0.65} mt={1.2}>
            <MDBox
                display="flex"
                alignItems="center"
                px={0.8}
                py={0.35}
                borderRadius="8px"
                sx={{
                    color: positive ? COLORS.green : COLORS.red,
                    background: positive ? COLORS.greenLight : COLORS.redLight,
                    fontSize: "12px",
                    fontWeight: 700,
                }}
            >
                {positive ? (
                    <ArrowUpward sx={{ fontSize: "14px !important", mr: 0.25 }} />
                ) : (
                    <ArrowDownward sx={{ fontSize: "14px !important", mr: 0.25 }} />
                )}
                {Math.abs(Number(value)).toFixed(1)}%
            </MDBox>
            <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                {label}
            </MDTypography>
        </MDBox>
    );
};

// Görünüm Finance kitindeki KPI kartıyla aynı; delta verilirse altta sapma rozeti
const BankingMetricCard = ({ delta = null, ...props }) => (
    <FinanceKpiTile
        {...props}
        footer={delta !== null && delta !== undefined ? <DeltaBadge value={delta} /> : null}
    />
);

const LineChart = ({ data, series, height = 350 }) => {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const width = 1200;
    const padding = { top: 28, right: 24, bottom: 44, left: 62 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const allValues = data.flatMap((item) =>
        series.map((serie) => Number(item[serie.key] || 0)),
    );
    const maxValue = Math.max(...allValues, 1);
    const magnitude = 10 ** Math.max(0, Math.floor(Math.log10(maxValue)) - 1);
    const roundedMax = Math.ceil(maxValue / magnitude) * magnitude || maxValue;

    const point = (value, index) => {
        const x =
            padding.left +
            (data.length <= 1
                ? chartWidth / 2
                : (index / (data.length - 1)) * chartWidth);
        const y =
            padding.top +
            chartHeight -
            (Number(value || 0) / roundedMax) * chartHeight;
        return { x, y };
    };

    const pathFor = (key) =>
        data
            .map((item, index) => {
                const p = point(item[key], index);
                return `${index === 0 ? "M" : "L"} ${p.x} ${p.y}`;
            })
            .join(" ");

    const areaPathFor = (key) => {
        if (!data.length) return "";
        const first = point(data[0][key], 0);
        const last = point(data[data.length - 1][key], data.length - 1);
        return `${pathFor(key)} L ${last.x} ${padding.top + chartHeight} L ${first.x} ${
            padding.top + chartHeight
        } Z`;
    };

    const getIndexFromMouse = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const scaleX = width / rect.width;
        const mouseX = (event.clientX - rect.left) * scaleX;
        const relativeX = Math.min(chartWidth, Math.max(0, mouseX - padding.left));
        const rawIndex =
            data.length <= 1
                ? 0
                : Math.round((relativeX / chartWidth) * (data.length - 1));
        setHoveredIndex(Math.min(data.length - 1, Math.max(0, rawIndex)));
    };

    const activeItem = hoveredIndex !== null ? data[hoveredIndex] : null;
    const activeX =
        hoveredIndex !== null
            ? point(activeItem?.[series[0]?.key], hoveredIndex).x
            : 0;

    const tooltipWidth = 180;
    const tooltipX =
        activeX > width - tooltipWidth - 24
            ? activeX - tooltipWidth - 14
            : activeX + 14;
    const gridValues = [0, 0.25, 0.5, 0.75, 1];

    return (
        <MDBox
            sx={{
                width: "100%",
                overflowX: "auto",
                position: "relative",
                userSelect: "none",
            }}
        >
            <svg
                viewBox={`0 0 ${width} ${height}`}
                width="100%"
                height={height}
                preserveAspectRatio="none"
                role="img"
                aria-label={financeText("Financial performance chart")}
                onMouseMove={getIndexFromMouse}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{
                    width: "100%",
                    height: "350px",
                    display: "block",
                }}
            >
                <defs>
                    <linearGradient id="revenueArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={COLORS.blue} stopOpacity="0.18" />
                        <stop offset="100%" stopColor={COLORS.blue} stopOpacity="0" />
                    </linearGradient>
                    <filter
                        id="tooltipShadow"
                        x="-20%"
                        y="-20%"
                        width="140%"
                        height="150%"
                    >
                        <feDropShadow
                            dx="0"
                            dy="7"
                            stdDeviation="7"
                            floodColor="#101828"
                            floodOpacity="0.16"
                        />
                    </filter>
                </defs>

                {gridValues.map((ratio) => {
                    const y = padding.top + chartHeight - ratio * chartHeight;
                    const value = roundedMax * ratio;
                    return (
                        <g key={ratio}>
                            <line
                                x1={padding.left}
                                y1={y}
                                x2={width - padding.right}
                                y2={y}
                                stroke="#EAECF0"
                                strokeWidth="1"
                                strokeDasharray={ratio === 0 ? "0" : "4 5"}
                            />
                            <text
                                x={padding.left - 12}
                                y={y + 4}
                                textAnchor="end"
                                fontSize="11"
                                fontWeight="600"
                                fill="#98A2B3"
                            >
                                {formatCompact(value)}
                            </text>
                        </g>
                    );
                })}

                {data.map((item, index) => {
                    const p = point(0, index);
                    const active = hoveredIndex === index;
                    return (
                        <text
                            key={`${item.label}-${index}`}
                            x={p.x}
                            y={height - 14}
                            textAnchor="middle"
                            fontSize="12"
                            fontWeight={active ? "700" : "500"}
                            fill={active ? COLORS.text : COLORS.subtext}
                        >
                            {item.label}
                        </text>
                    );
                })}

                {series[0] && (
                    <path d={areaPathFor(series[0].key)} fill="url(#revenueArea)" />
                )}

                {series.map((serie) => (
                    <g key={serie.key}>
                        <path
                            d={pathFor(serie.key)}
                            fill="none"
                            stroke={serie.color}
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        {data.map((item, index) => {
                            const p = point(item[serie.key], index);
                            const active = hoveredIndex === index;
                            return (
                                <g key={`${serie.key}-${index}`}>
                                    <circle
                                        cx={p.x}
                                        cy={p.y}
                                        r={active ? "7" : "5.5"}
                                        fill="#FFFFFF"
                                        stroke={active ? serie.color : "transparent"}
                                        strokeWidth="2"
                                    />
                                    <circle
                                        cx={p.x}
                                        cy={p.y}
                                        r={active ? "4" : "3"}
                                        fill={serie.color}
                                    />
                                </g>
                            );
                        })}
                    </g>
                ))}

                {activeItem && (
                    <g pointerEvents="none">
                        <line
                            x1={activeX}
                            y1={padding.top}
                            x2={activeX}
                            y2={padding.top + chartHeight}
                            stroke="#98A2B3"
                            strokeWidth="1.2"
                            strokeDasharray="4 4"
                        />

                        <g
                            transform={`translate(${tooltipX}, 18)`}
                            filter="url(#tooltipShadow)"
                        >
                            <rect
                                width={tooltipWidth}
                                height={142}
                                rx="12"
                                fill="#FFFFFF"
                                stroke="#E4E7EC"
                            />
                            <text
                                x="14"
                                y="23"
                                fontSize="12"
                                fontWeight="700"
                                fill={COLORS.text}
                            >
                                {activeItem.label} {financeText("financial summary")}
                            </text>
                            <line
                                x1="14"
                                y1="34"
                                x2={tooltipWidth - 14}
                                y2="34"
                                stroke="#EAECF0"
                            />

                            {series.map((serie, index) => (
                                <g
                                    key={serie.key}
                                    transform={`translate(0, ${48 + index * 25})`}
                                >
                                    <circle cx="17" cy="0" r="4" fill={serie.color} />
                                    <text
                                        x="29"
                                        y="4"
                                        fontSize="11"
                                        fontWeight="600"
                                        fill={COLORS.subtext}
                                    >
                                        {financeText(serie.label || serie.key)}
                                    </text>
                                    <text
                                        x={tooltipWidth - 14}
                                        y="4"
                                        textAnchor="end"
                                        fontSize="11"
                                        fontWeight="700"
                                        fill={COLORS.text}
                                    >
                                        {formatCompact(activeItem[serie.key])}
                                    </text>
                                </g>
                            ))}

                            <g transform={`translate(0, ${48 + series.length * 25})`}>
                                <line
                                    x1="14"
                                    y1="-10"
                                    x2={tooltipWidth - 14}
                                    y2="-10"
                                    stroke="#EAECF0"
                                />
                                <text
                                    x="14"
                                    y="7"
                                    fontSize="11"
                                    fontWeight="600"
                                    fill={COLORS.subtext}
                                >
                                    {financeText("Net profit")}
                                </text>
                                <text
                                    x={tooltipWidth - 14}
                                    y="7"
                                    textAnchor="end"
                                    fontSize="11"
                                    fontWeight="700"
                                    fill={
                                        Number(activeItem.profit || 0) >= 0
                                            ? COLORS.green
                                            : COLORS.red
                                    }
                                >
                                    {formatCompact(activeItem.profit)}
                                </text>
                            </g>
                        </g>
                    </g>
                )}

                <rect
                    x={padding.left}
                    y={padding.top}
                    width={chartWidth}
                    height={chartHeight}
                    fill="transparent"
                />
            </svg>
        </MDBox>
    );
};

const DonutChart = ({
                        value,
                        size = 172,
                        strokeWidth = 17,
                        label,
                        helper,
                        color = COLORS.blue,
                    }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const normalized = Math.min(100, Math.max(0, Number(value || 0)));
    const offset = circumference - (normalized / 100) * circumference;

    return (
        <MDBox position="relative" width={size} height={size} mx="auto">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="#EEF2F6"
                    strokeWidth={strokeWidth}
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={color}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
            </svg>

            <MDBox
                position="absolute"
                top="50%"
                left="50%"
                sx={{ transform: "translate(-50%, -50%)" }}
                textAlign="center"
            >
                <MDTypography
                    variant="h3"
                    fontWeight="bold"
                    sx={{ color: COLORS.text }}
                >
                    {Math.round(normalized)}%
                </MDTypography>
                <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    sx={{ color: COLORS.subtext }}
                >
                    {label}
                </MDTypography>
                {helper && (
                    <MDTypography
                        variant="caption"
                        display="block"
                        sx={{ color: "#98A2B3", fontSize: "10px" }}
                    >
                        {helper}
                    </MDTypography>
                )}
            </MDBox>
        </MDBox>
    );
};

const AgingBar = ({ label, amount, total, color, count }) => {
    const rate = total ? Math.min((amount / total) * 100, 100) : 0;

    return (
        <MDBox mb={2}>
            <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={0.7}
            >
                <MDBox>
                    <MDTypography
                        variant="button"
                        fontWeight="bold"
                        sx={{ color: COLORS.text }}
                    >
                        {financeText(label)}
                    </MDTypography>
                    <MDTypography
                        variant="caption"
                        display="block"
                        sx={{ color: COLORS.subtext }}
                    >
                        {count} {financeText(count === 1 ? "invoice" : "invoices")}
                    </MDTypography>
                </MDBox>
                <MDTypography
                    variant="button"
                    fontWeight="bold"
                    sx={{ color: COLORS.text }}
                >
                    {formatCompact(amount)}
                </MDTypography>
            </MDBox>
            <LinearProgress
                variant="determinate"
                value={rate}
                sx={{
                    height: 8,
                    borderRadius: "999px",
                    backgroundColor: "#F2F4F7",
                    "& .MuiLinearProgress-bar": {
                        borderRadius: "999px",
                        background: color,
                    },
                }}
            />
        </MDBox>
    );
};

const QuickAction = ({ icon, title, subtitle, onClick, tone = "blue" }) => {
    const palette = {
        blue: { color: COLORS.blue, bg: COLORS.blueLight },
        green: { color: COLORS.green, bg: COLORS.greenLight },
        red: { color: COLORS.red, bg: COLORS.redLight },
        amber: { color: COLORS.amber, bg: COLORS.amberLight },
    };

    const selected = palette[tone] || palette.blue;

    return (
        <ButtonBase
            onClick={onClick}
            sx={{
                width: "100%",
                borderRadius: "8px",
                textAlign: "left",
                p: 1.5,
                justifyContent: "space-between",
                border: `1px solid ${COLORS.border}`,
                transition: ".2s ease",
                "&:hover": {
                    borderColor: "#98A2B3",
                    background: "#F9FAFB",
                    transform: "translateX(2px)",
                },
            }}
        >
            <MDBox display="flex" alignItems="center" gap={1.3}>
                <MDBox
                    width={42}
                    height={42}
                    borderRadius="8px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ color: selected.color, background: selected.bg }}
                >
                    {icon}
                </MDBox>
                <MDBox>
                    <MDTypography
                        variant="button"
                        fontWeight="bold"
                        sx={{ color: COLORS.text }}
                    >
                        {title}
                    </MDTypography>
                    <MDTypography
                        variant="caption"
                        display="block"
                        sx={{ color: COLORS.subtext }}
                    >
                        {subtitle}
                    </MDTypography>
                </MDBox>
            </MDBox>
            <ArrowForward fontSize="small" sx={{ color: COLORS.subtext }} />
        </ButtonBase>
    );
};

const ModeBarChart = ({ data, metric = "revenue", currency = "EUR" }) => {
    const maxValue = Math.max(
        ...data.map((item) => Number(item[metric] || 0)),
        1,
    );

    return (
        <Stack spacing={2.1}>
            {data.map((item) => {
                const value = Number(item[metric] || 0);
                const percentage = Math.min((value / maxValue) * 100, 100);
                const modeColor =
                    item.mode === "Ocean"
                        ? COLORS.blue
                        : item.mode === "Road"
                            ? COLORS.amber
                            : item.mode === "Air"
                                ? COLORS.purple
                                : COLORS.subtext;

                return (
                    <MDBox key={item.mode}>
                        <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={0.8}
                        >
                            <MDBox display="flex" alignItems="center" gap={1}>
                                <MDBox
                                    width={10}
                                    height={10}
                                    borderRadius="50%"
                                    sx={{ background: modeColor }}
                                />
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.text }}
                                >
                                    {financeText(item.mode)}
                                </MDTypography>
                                <Chip
                                    size="small"
                                    label={`${item.jobs} ${financeText("jobs")}`}
                                    sx={{
                                        height: 22,
                                        fontSize: "10px",
                                        fontWeight: 700,
                                        background: "#F2F4F7",
                                        color: COLORS.subtext,
                                    }}
                                />
                            </MDBox>
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: COLORS.text }}
                            >
                                {metric === "jobs"
                                    ? formatNumber(value)
                                    : formatCompact(value, currency)}
                            </MDTypography>
                        </MDBox>
                        <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{
                                height: 10,
                                borderRadius: "999px",
                                backgroundColor: "#F2F4F7",
                                "& .MuiLinearProgress-bar": {
                                    borderRadius: "999px",
                                    background: modeColor,
                                },
                            }}
                        />
                    </MDBox>
                );
            })}
        </Stack>
    );
};

const ShipmentTypeBars = ({ data, total }) => (
    <Stack spacing={1.65}>
        {data.map((item, index) => {
            const share = total ? (item.count / total) * 100 : 0;
            const barColor = [
                COLORS.blue,
                COLORS.purple,
                COLORS.amber,
                COLORS.green,
                COLORS.cyan,
            ][index % 5];

            return (
                <MDBox key={`${item.mode}::${item.type}`}>
                    <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={0.65}
                    >
                        <MDBox display="flex" alignItems="center" gap={1}>
                            <MDBox
                                width={30}
                                height={30}
                                borderRadius="9px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                sx={{ background: "#F2F4F7", color: COLORS.navy }}
                            >
                                <Inventory2 sx={{ fontSize: "16px !important" }} />
                            </MDBox>
                            <MDBox>
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.text }}
                                >
                                    {item.type}
                                </MDTypography>
                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: COLORS.subtext }}
                                >
                                    {item.modeLabel}
                                </MDTypography>
                            </MDBox>
                        </MDBox>
                        <MDBox textAlign="right">
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: COLORS.text }}
                            >
                                {item.count}
                            </MDTypography>
                            <MDTypography
                                variant="caption"
                                display="block"
                                sx={{ color: COLORS.subtext }}
                            >
                                {share.toFixed(1)}%
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(share, 100)}
                        sx={{
                            height: 6,
                            borderRadius: "999px",
                            backgroundColor: "#F2F4F7",
                            "& .MuiLinearProgress-bar": {
                                borderRadius: "999px",
                                background: barColor,
                            },
                        }}
                    />
                </MDBox>
            );
        })}
    </Stack>
);

const MonthlyIncomeExpenseTable = ({
                                       rows,
                                       language,
                                       currency,
                                       exchangeRates,
                                       exchangeRateUpdatedAt,
                                       loadingRates,
                                       onExport,
                                   }) => {
    const tableData = useMemo(() => {
        const totals = rows.reduce(
            (acc, item) => ({
                grossRevenue: acc.grossRevenue + Number(item.grossRevenue || 0),
                returned: acc.returned + Number(item.returned || 0),
                revenue: acc.revenue + Number(item.revenue || 0),
                cost: acc.cost + Number(item.cost || 0),
                profit: acc.profit + Number(item.profit || 0),
                jobCount: acc.jobCount + Number(item.jobCount || 0),
            }),
            {
                grossRevenue: 0,
                returned: 0,
                revenue: 0,
                cost: 0,
                profit: 0,
                jobCount: 0,
            },
        );

        const totalMargin = totals.revenue
            ? (totals.profit / totals.revenue) * 100
            : 0;

        const dataRows = rows.map((item) => ({
            ...item,
            isTotal: false,
            margin: item.revenue ? (item.profit / item.revenue) * 100 : 0,
        }));

        dataRows.push({
            key: "total",
            label: financeText("TOTAL"),
            grossRevenue: totals.grossRevenue,
            returned: totals.returned,
            revenue: totals.revenue,
            cost: totals.cost,
            profit: totals.profit,
            margin: totalMargin,
            jobCount: totals.jobCount,
            isTotal: true,
        });

        return {
            columns: [
                {
                    Header: financeText("MONTH"),
                    accessor: "label",
                    width: "24%",
                    Cell: ({ row }) => {
                        const item = row.original;

                        return (
                            <MDBox display="flex" alignItems="center" gap={1.2} py={0.4}>
                                <MDBox
                                    width={40}
                                    height={40}
                                    borderRadius="8px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                    sx={{
                                        background: item.isTotal ? COLORS.navy : COLORS.blueLight,
                                        color: item.isTotal ? COLORS.white : COLORS.blue,
                                        border: item.isTotal ? "none" : "1px solid #B2DDFF",
                                    }}
                                >
                                    {item.isTotal ? (
                                        <AccountBalanceWallet
                                            sx={{ fontSize: "19px !important" }}
                                        />
                                    ) : (
                                        <CalendarMonth sx={{ fontSize: "19px !important" }} />
                                    )}
                                </MDBox>

                                <MDBox minWidth={0}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        sx={{ color: item.isTotal ? COLORS.navy : COLORS.text }}
                                    >
                                        {item.label}
                                    </MDTypography>
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {item.jobCount} {financeText(item.isTotal ? "total records" : "records")}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("NET REVENUE"),
                    accessor: "revenue",
                    align: "right",
                    width: "17%",
                    Cell: ({ row }) => {
                        const item = row.original;

                        return (
                            <MDBox textAlign="right">
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: item.isTotal ? COLORS.navy : COLORS.blue }}
                                >
                                    {formatMoney(item.revenue, currency)}
                                </MDTypography>
                                {!item.isTotal && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("Gross")} {formatCompact(item.grossRevenue, currency)}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("ISSUED RETURNS"),
                    accessor: "returned",
                    align: "right",
                    width: "16%",
                    Cell: ({ row }) => {
                        const item = row.original;

                        return (
                            <MDBox textAlign="right">
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                            Number(item.returned || 0) > 0
                                                ? COLORS.purple
                                                : COLORS.subtext,
                                    }}
                                >
                                    {formatMoney(item.returned, currency)}
                                </MDTypography>
                                {!item.isTotal && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("Revenue reduction")}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("EXPENSE / COST"),
                    accessor: "cost",
                    align: "right",
                    width: "17%",
                    Cell: ({ row }) => {
                        const item = row.original;

                        return (
                            <MDBox textAlign="right">
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.red }}
                                >
                                    {formatMoney(item.cost, currency)}
                                </MDTypography>
                                {!item.isTotal && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("Supplier + operation cost")}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("STATUS / PROFIT"),
                    accessor: "profit",
                    align: "right",
                    width: "16%",
                    Cell: ({ row }) => {
                        const item = row.original;
                        const positive = Number(item.profit || 0) >= 0;

                        return (
                            <MDBox
                                display="flex"
                                justifyContent="flex-end"
                                alignItems="center"
                                gap={0.55}
                            >
                                {positive ? (
                                    <ArrowUpward
                                        sx={{ fontSize: "15px !important", color: COLORS.green }}
                                    />
                                ) : (
                                    <ArrowDownward
                                        sx={{ fontSize: "15px !important", color: COLORS.red }}
                                    />
                                )}
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: positive ? COLORS.green : COLORS.red }}
                                >
                                    {formatMoney(item.profit, currency)}
                                </MDTypography>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("MARGIN"),
                    accessor: "margin",
                    align: "right",
                    width: "10%",
                    Cell: ({ row }) => {
                        const item = row.original;
                        const positive = Number(item.profit || 0) >= 0;

                        return (
                            <MDBox display="flex" justifyContent="flex-end">
                                <Chip
                                    size="small"
                                    label={`${Number(item.margin || 0).toFixed(1)}%`}
                                    sx={{
                                        minWidth: 72,
                                        fontWeight: 800,
                                        background: positive ? COLORS.greenLight : COLORS.redLight,
                                        color: positive ? COLORS.green : COLORS.red,
                                        border: `1px solid ${positive ? "#ABEFC6" : "#FECDCA"}`,
                                    }}
                                />
                            </MDBox>
                        );
                    },
                },
            ],
            rows: dataRows,
        };
    }, [rows, currency, language]);

    return (
        <Card sx={{ ...cardSx, mt: 2.5 }}>
            <MDBox p={3}>
                <SectionHeader
                    title={financeText("Monthly Revenue / Returns / Expense")}
                    subtitle={financeText("Gross selling, issued returns, net revenue, cost and profitability through the current month")}
                    action={
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                            <Chip
                                icon={
                                    <CurrencyExchange
                                        sx={{ color: `${COLORS.blue} !important` }}
                                    />
                                }
                                label={`${financeText("Report Currency")}: ${currency}`}
                                sx={{
                                    background: COLORS.blueLight,
                                    color: COLORS.blue,
                                    fontWeight: 800,
                                    border: "1px solid #B2DDFF",
                                }}
                            />
                            <MDButton
                                variant="contained"
                                color="success"
                                onClick={onExport}
                                disabled={loadingRates}
                                sx={{
                                    borderRadius: "10px",
                                    textTransform: "none",
                                    boxShadow: "none",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <Download sx={{ mr: 0.6 }} />
                                {financeText("Export to Excel")}
                            </MDButton>
                        </Stack>
                    }
                />

                <MDBox
                    sx={{
                        "& .MuiTableContainer-root": {
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: "10px",
                            overflowX: "auto",
                            boxShadow: "none",
                        },
                        "& table": {
                            minWidth: 980,
                            borderCollapse: "separate",
                            borderSpacing: 0,
                        },
                        "& table thead": {
                            background: "#F8FAFC",
                        },
                        "& table thead th": {
                            background: "transparent",
                            color: "#475467 !important",
                            fontSize: "10px !important",
                            fontWeight: "800 !important",
                            letterSpacing: ".085em",
                            paddingTop: "15px !important",
                            paddingBottom: "15px !important",
                            borderBottom: `1px solid ${COLORS.border} !important`,
                        },
                        "& table tbody td": {
                            paddingTop: "13px !important",
                            paddingBottom: "13px !important",
                            borderBottom: "1px solid #F0F2F5 !important",
                            verticalAlign: "middle",
                        },
                        "& table tbody tr": {
                            background: "#FFFFFF",
                            transition: "background .18s ease, box-shadow .18s ease",
                        },
                        "& table tbody tr:nth-of-type(even)": {
                            background: "#FCFCFD",
                        },
                        "& table tbody tr:hover": {
                            background: "#F5F8FF !important",
                            boxShadow: "inset 3px 0 0 #175CD3",
                        },
                        "& table tbody tr:last-child": {
                            background:
                                "#F2F4F7 !important",
                        },
                        "& table tbody tr:last-child td": {
                            borderTop: "2px solid #D0D5DD !important",
                            borderBottom: "0 !important",
                        },
                    }}
                >
                    <DataTable
                        table={tableData}
                        entriesPerPage={false}
                        showTotalEntries={false}
                        isSorted={false}
                        noEndBorder
                    />
                </MDBox>

                <MDBox
                    mt={2}
                    p={2}
                    borderRadius="8px"
                    sx={{
                        background: "#F9FAFB",
                        border: `1px solid ${COLORS.border}`,
                    }}
                >
                    <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", md: "center" }}
                        flexDirection={{ xs: "column", md: "row" }}
                        gap={1.5}
                    >
                        <MDBox display="flex" alignItems="center" gap={1.2}>
                            <MDBox
                                width={42}
                                height={42}
                                borderRadius="8px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                sx={{ background: COLORS.blueLight, color: COLORS.blue }}
                            >
                                <CurrencyExchange />
                            </MDBox>
                            <MDBox>
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.text }}
                                >
                                    {financeText("Current Exchange Rates")}
                                </MDTypography>
                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: COLORS.subtext }}
                                >
                                    {financeText("Last updated")}:{" "}
                                    {exchangeRateUpdatedAt
                                        ? new Date(exchangeRateUpdatedAt).toLocaleString("en-US")
                                        : financeText("Exchange rate information unavailable")}
                                </MDTypography>
                            </MDBox>
                        </MDBox>

                        <MDBox display="flex" flexWrap="wrap" gap={1}>
                            {["EUR", "USD", "GBP", "TRY"].map((rateCurrency) => {
                                const rate = exchangeRates?.[rateCurrency];

                                return (
                                    <Chip
                                        key={rateCurrency}
                                        label={`${rateCurrency}: ${
                                            Number.isFinite(Number(rate))
                                                ? Number(rate).toFixed(4)
                                                : "-"
                                        }`}
                                        sx={{
                                            background:
                                                rateCurrency === currency
                                                    ? COLORS.blueLight
                                                    : COLORS.white,
                                            color:
                                                rateCurrency === currency ? COLORS.blue : COLORS.text,
                                            border: `1px solid ${rateCurrency === currency ? "#B2DDFF" : COLORS.border}`,
                                            fontWeight: 800,
                                        }}
                                    />
                                );
                            })}
                        </MDBox>
                    </MDBox>
                </MDBox>
            </MDBox>
        </Card>
    );
};
const FinanceDashboard = () => {
    const navigate = useNavigate();
    const [financeLanguage, setFinanceLanguageState] = useState(getFinanceLanguage);

    const changeFinanceLanguage = (event) => {
        const nextLanguage = event.target.value === "tr" ? "tr" : "en";
        saveFinanceLanguage(nextLanguage);
        setFinanceLanguageState(nextLanguage);
    };

    const [financeJobs, setFinanceJobs] = useState([]);
    const [businessPartners, setBusinessPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState("6M");
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const now = new Date();
    const currentYear = now.getFullYear();
    const todayValue = `${currentYear}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const [reportStartDate, setReportStartDate] = useState(
        `${currentYear}-01-01`,
    );
    const [reportEndDate, setReportEndDate] = useState(todayValue);
    const [selectedParty, setSelectedParty] = useState(null);
    const [selectedTransportMode, setSelectedTransportMode] = useState("ALL");
    const [selectedInvoiceOrigin, setSelectedInvoiceOrigin] = useState("ALL");
    const [selectedReturnStatus, setSelectedReturnStatus] = useState("ALL");
    const [selectedCollectionRisk, setSelectedCollectionRisk] = useState("ALL");
    const [selectedGeographicScope, setSelectedGeographicScope] = useState("ALL");
    const [selectedCurrency, setSelectedCurrency] = useState("EUR");
    const [exchangeRates, setExchangeRates] = useState(null);
    const [exchangeRateUpdatedAt, setExchangeRateUpdatedAt] = useState(null);
    const [loadingRates, setLoadingRates] = useState(false);
    const [profitDrilldown, setProfitDrilldown] = useState(null);

    const token = localStorage.getItem("token");

    const fetchWithAuth = useCallback(
        async (path) => {
            const response = await fetch(`${API_URL}${path}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) throw new Error(`${path} fetch failed`);

            const data = await response.json();
            const candidates = [
                data,
                data?.data,
                data?.jobs,
                data?.invoices,
                data?.results,
                data?.records,
                data?.returnInvoices,
                data?.return_invoices,
                data?.customers,
                data?.serviceProviders,
                data?.service_providers,
                data?.globalAgents,
                data?.global_agents,
                data?.data?.data,
                data?.data?.jobs,
                data?.data?.invoices,
                data?.data?.results,
                data?.data?.records,
                data?.data?.returnInvoices,
                data?.data?.return_invoices,
            ];

            return candidates.find(Array.isArray) || [];
        },
        [token],
    );

    const fetchFinanceJobs = useCallback(async () => {
        try {
            setLoading(true);

            const financeDataPromise = fetchWithAuth("/finance/jobs").catch(
                async (financeError) => {
                    console.warn(
                        "Finance endpoint failed, fallback to /invoice:",
                        financeError,
                    );
                    return fetchWithAuth("/invoice");
                },
            );
            const manualInvoicesPromise = fetchWithAuth(
                "/finance/manual-invoices?invoiceType=sales&limit=200",
            ).catch((manualInvoiceError) => {
                console.warn(
                    "Manual invoices could not be loaded:",
                    manualInvoiceError,
                );
                return [];
            });
            const returnInvoicesPromise = fetchWithAuth(
                "/finance/return-invoices?invoiceType=sales",
            ).catch((returnInvoiceError) => {
                console.warn(
                    "Return invoices could not be loaded:",
                    returnInvoiceError,
                );
                return [];
            });

            const [financeData, manualInvoices, returnInvoices] = await Promise.all([
                financeDataPromise,
                manualInvoicesPromise,
                returnInvoicesPromise,
            ]);
            const mergedRecords = [...manualInvoices, ...financeData].filter(
                (record) =>
                    !record?.invoiceType ||
                    String(record.invoiceType).toLowerCase() === "sales",
            );
            const uniqueRecords = new Map();

            mergedRecords.forEach((record, index) => {
                const financeJobId = getEntityId(
                    record?.financeJobId ||
                    record?.finance_job_id ||
                    record?.jobFinanceId ||
                    record?.job_finance_id,
                );
                const nestedInvoices = getCustomerInvoices(record);
                const recordInvoices = nestedInvoices.length
                    ? nestedInvoices
                    : financeJobId && getInvoiceNo(record)
                        ? [record]
                        : [];
                const key =
                    financeJobId ||
                    record?.invoiceId ||
                    record?.invoice_id ||
                    record?._id ||
                    record?.id ||
                    `finance-record-${index}`;
                const previous = uniqueRecords.get(String(key)) || {};
                const mergedInvoices = [
                    ...getCustomerInvoices(previous),
                    ...recordInvoices,
                ];
                const uniqueInvoices = Array.from(
                    new Map(
                        mergedInvoices.map((invoice, invoiceIndex) => [
                            getEntityId(
                                invoice?._id ||
                                invoice?.id ||
                                invoice?.invoiceId ||
                                invoice?.invoice_id,
                            ) ||
                            normalizeLookupKey(getInvoiceNo(invoice)) ||
                            `${key}-invoice-${invoiceIndex}`,
                            invoice,
                        ]),
                    ).values(),
                );

                uniqueRecords.set(String(key), {
                    ...previous,
                    ...record,
                    ...(financeJobId ? { financeJobId } : {}),
                    customerInvoices: uniqueInvoices,
                });
            });

            const uniqueReturnInvoices = Array.from(
                new Map(
                    returnInvoices.map((returnInvoice, index) => [
                        getEntityId(returnInvoice?._id || returnInvoice?.id) ||
                        `return-${index}`,
                        returnInvoice,
                    ]),
                ).values(),
            );
            const returnsByOriginalId = new Map();
            const returnsByOriginalNo = new Map();

            uniqueReturnInvoices.forEach((returnInvoice) => {
                const originalInvoiceId = getOriginalInvoiceId(returnInvoice);
                const originalInvoiceNo = normalizeLookupKey(
                    getOriginalInvoiceNo(returnInvoice),
                );

                if (originalInvoiceId) {
                    returnsByOriginalId.set(originalInvoiceId, [
                        ...(returnsByOriginalId.get(originalInvoiceId) || []),
                        returnInvoice,
                    ]);
                }

                if (originalInvoiceNo) {
                    returnsByOriginalNo.set(originalInvoiceNo, [
                        ...(returnsByOriginalNo.get(originalInvoiceNo) || []),
                        returnInvoice,
                    ]);
                }
            });

            const enrichedRecords = Array.from(uniqueRecords.values()).map(
                (record) => {
                    const customerInvoices = getCustomerInvoices(record).map(
                        (invoice) => {
                            const invoiceIdentityKeys = [
                                invoice?._id,
                                invoice?.id,
                                invoice?.invoiceId,
                                invoice?.invoice_id,
                            ]
                                .map(getEntityId)
                                .filter(Boolean);
                            const invoiceNoKey = normalizeLookupKey(getInvoiceNo(invoice));
                            const invoiceReturns = [
                                ...getLinkedReturnInvoices(invoice),
                                ...invoiceIdentityKeys.flatMap(
                                    (identityKey) => returnsByOriginalId.get(identityKey) || [],
                                ),
                                ...(returnsByOriginalNo.get(invoiceNoKey) || []),
                            ];

                            return {
                                ...invoice,
                                returnInvoices: Array.from(
                                    new Map(
                                        invoiceReturns.map((returnInvoice, returnIndex) => [
                                            getEntityId(returnInvoice?._id || returnInvoice?.id) ||
                                            `${invoiceNoKey || "invoice"}-return-${returnIndex}`,
                                            returnInvoice,
                                        ]),
                                    ).values(),
                                ),
                            };
                        },
                    );
                    const identityKeys = [
                        record?._id,
                        record?.id,
                        record?.invoiceId,
                        record?.invoice_id,
                        record?.customerInvoiceId,
                        record?.customer_invoice_id,
                        ...customerInvoices.flatMap((invoice) => [
                            invoice?._id,
                            invoice?.id,
                            invoice?.invoiceId,
                            invoice?.invoice_id,
                        ]),
                    ]
                        .map(getEntityId)
                        .filter(Boolean);
                    const invoiceNoKey = normalizeLookupKey(getInvoiceNo(record));
                    const linkedReturns = [
                        ...getLinkedReturnInvoices(record),
                        ...customerInvoices.flatMap(getLinkedReturnInvoices),
                        ...identityKeys.flatMap(
                            (identityKey) => returnsByOriginalId.get(identityKey) || [],
                        ),
                        ...(returnsByOriginalNo.get(invoiceNoKey) || []),
                    ];
                    const uniqueLinkedReturns = Array.from(
                        new Map(
                            linkedReturns.map((returnInvoice, index) => [
                                getEntityId(returnInvoice?._id || returnInvoice?.id) ||
                                `${invoiceNoKey || "invoice"}-return-${index}`,
                                returnInvoice,
                            ]),
                        ).values(),
                    );

                    return {
                        ...record,
                        customerInvoices,
                        returnInvoices: uniqueLinkedReturns,
                    };
                },
            );

            setFinanceJobs(enrichedRecords);

            setLastUpdated(new Date());
        } catch (error) {
            console.error("Finance dashboard fetch error:", error);
            setFinanceJobs([]);
        } finally {
            setLoading(false);
        }
    }, [fetchWithAuth]);

    const fetchBusinessPartners = useCallback(async () => {
        const fetchFirstAvailable = async (paths) => {
            for (const path of paths) {
                try {
                    return await fetchWithAuth(path);
                } catch (error) {
                    console.warn(`${path} lookup failed`, error);
                }
            }

            return [];
        };

        const [customers, serviceProviders, globalAgents] = await Promise.all([
            fetchFirstAvailable(["/customer", "/customers"]),
            fetchFirstAvailable(["/serviceProvider", "/service-provider"]),
            fetchFirstAvailable(["/globalAgent", "/global-agent"]),
        ]);

        setBusinessPartners([...customers, ...serviceProviders, ...globalAgents]);
    }, [fetchWithAuth]);

    const fetchExchangeRates = useCallback(async () => {
        setLoadingRates(true);

        try {
            const response = await fetch(
                "https://api.exchangerate-api.com/v4/latest/USD",
            );

            if (!response.ok) {
                throw new Error("Failed to fetch exchange rates");
            }

            const data = await response.json();

            if (!data?.rates) {
                throw new Error("Exchange-rate response does not contain rates");
            }

            setExchangeRates(data.rates);
            setExchangeRateUpdatedAt(
                data?.date || data?.time_last_updated || new Date().toISOString(),
            );
        } catch (error) {
            console.error("Error loading exchange rates:", error);
            setExchangeRates(null);
            setExchangeRateUpdatedAt(null);
        } finally {
            setLoadingRates(false);
        }
    }, []);

    useEffect(() => {
        fetchFinanceJobs();
        fetchBusinessPartners();
        fetchExchangeRates();
    }, [fetchFinanceJobs, fetchBusinessPartners, fetchExchangeRates]);

    const resolveFinanceRecord = async (job) => {
        const recordIds = [
            job?.financeJobId,
            job?.finance_job_id,
            job?.jobFinanceId,
            job?.job_finance_id,
            job?._id,
            job?.id,
        ].filter(Boolean);
        const uniqueIds = [...new Set(recordIds.map(String))];

        for (const recordId of uniqueIds) {
            const detailPaths = [
                `/finance/manual-invoices/${recordId}`,
                `/invoice/${recordId}`,
                `/finance/jobs/${recordId}`,
                `/finance/customer-invoices/${recordId}`,
            ];

            for (const path of detailPaths) {
                try {
                    const response = await fetch(`${API_URL}${path}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    if (!response.ok) continue;

                    const payload = await response.json();
                    const detail =
                        payload?.data?.invoice ||
                        payload?.data?.job ||
                        payload?.data?.data ||
                        payload?.invoice ||
                        payload?.job ||
                        payload?.data ||
                        payload;

                    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
                        return {
                            ...job,
                            ...detail,
                            invoiceItems:
                                detail?.invoiceItems ||
                                detail?.invoice_items ||
                                job?.invoiceItems ||
                                job?.invoice_items ||
                                [],
                        };
                    }
                } catch (error) {
                    console.warn(`${path} finance detail lookup failed`, error);
                }
            }
        }

        return job;
    };

    const openFinanceDetail = async (job) => {
        const resolvedJob = await resolveFinanceRecord(job);
        const financeJobId =
            resolvedJob?.financeJobId ||
            resolvedJob?.finance_job_id ||
            resolvedJob?.jobFinanceId ||
            resolvedJob?.job_finance_id ||
            resolvedJob?._id ||
            resolvedJob?.id;

        if (!financeJobId) {
            appAlert(financeText("Finance Job ID not found!"));
            return;
        }

        if (getInvoiceOrigin(resolvedJob) === "manual") {
            navigate(`/invoice/finance/jobs/${financeJobId}`, {
                state: {
                    job: resolvedJob,
                    financeJobId,
                    invoiceOrigin: "manual",
                },
            });
            return;
        }

        navigate(`/invoice/finance/jobs/${financeJobId}`, {
            state: {
                job: resolvedJob,
                financeJobId,
            },
        });
    };

    const openInvoiceEditor = async (job) => {
        const resolvedJob = await resolveFinanceRecord(job);
        const manualInvoiceId =
            resolvedJob?._id ||
            resolvedJob?.id ||
            resolvedJob?.invoiceId ||
            resolvedJob?.invoice_id;

        if (getInvoiceOrigin(resolvedJob) === "manual" && manualInvoiceId) {
            navigate(`/invoice/finance/manual-invoices/${manualInvoiceId}/edit`, {
                state: {
                    invoice: resolvedJob,
                    invoiceId: manualInvoiceId,
                    editMode: true,
                },
            });
            return;
        }

        const shipmentId =
            resolvedJob?.shipmentId ||
            resolvedJob?.shipment_id ||
            resolvedJob?._id ||
            resolvedJob?.id;

        if (!shipmentId) {
            appAlert(financeText("Shipment ID not found!"));
            return;
        }

        const sellingCurrency = getCurrency(resolvedJob);
        const costCurrency = getCostCurrency(resolvedJob);
        const sourceItems =
            [
                resolvedJob?.invoiceItems,
                resolvedJob?.invoice_items,
                resolvedJob?.cargoDetails,
                resolvedJob?.cargo_details,
            ].find(Array.isArray) || [];
        const normalizedItems = sourceItems.map((item) => {
            const rawItem = item?.attributes || item;
            const unitQuantity =
                firstDefinedNumber(
                    rawItem?.unitQuantity,
                    rawItem?.unit_quantity,
                    rawItem?.quantity,
                    rawItem?.qty,
                ) || 1;
            const explicitUnitPrice = firstDefinedNumber(
                rawItem?.unitPrice,
                rawItem?.unit_price,
                rawItem?.salesPrice,
                rawItem?.price,
            );
            const explicitSellingRate = firstDefinedNumber(
                rawItem?.sellingRate,
                rawItem?.selling_rate,
                rawItem?.salesAmount,
            );
            const amountBasedUnitPrice =
                firstDefinedNumber(rawItem?.amount) / unitQuantity;
            const unitPrice =
                explicitUnitPrice > 0
                    ? explicitUnitPrice
                    : explicitSellingRate > 0
                        ? explicitSellingRate
                        : amountBasedUnitPrice;

            return {
                ...rawItem,
                commodity:
                    rawItem?.commodity ||
                    rawItem?.invoiceItem ||
                    rawItem?.invoice_item ||
                    rawItem?.label ||
                    "",
                description: rawItem?.description || "",
                totalCost: firstDefinedNumber(
                    rawItem?.totalCost,
                    rawItem?.total_cost,
                    rawItem?.cost,
                    rawItem?.unitCost,
                    rawItem?.unit_cost,
                ),
                costCurrency: normalizeCurrencyCode(
                    rawItem?.costCurrency || rawItem?.cost_currency || costCurrency,
                ),
                unitPrice,
                sellingRate: unitPrice,
                sellingRateCurrency: normalizeCurrencyCode(
                    rawItem?.sellingRateCurrency ||
                    rawItem?.selling_rate_currency ||
                    rawItem?.currency ||
                    sellingCurrency,
                ),
                unitQuantity,
                amount: unitPrice * unitQuantity,
            };
        });
        const invoiceItems = normalizedItems.length
            ? normalizedItems
            : [
                {
                    commodity: "Freight Charge",
                    description: "Shipment freight service",
                    totalCost: getCost(resolvedJob),
                    costCurrency,
                    unitPrice: getRevenue(resolvedJob),
                    sellingRate: getRevenue(resolvedJob),
                    sellingRateCurrency: sellingCurrency,
                    unitQuantity: 1,
                    amount: getRevenue(resolvedJob),
                },
            ];
        const detectedMode = getTransportMode(resolvedJob).toLowerCase();
        const sourceType = ["road", "ocean", "air"].includes(detectedMode)
            ? detectedMode
            : "";

        navigate("/create-invoice", {
            state: {
                openInvoiceModal: true,
                editMode: true,
                invoiceId:
                    resolvedJob?._id ||
                    resolvedJob?.id ||
                    resolvedJob?.invoiceId ||
                    resolvedJob?.invoice_id ||
                    null,
                financeJobId:
                    resolvedJob?.financeJobId ||
                    resolvedJob?.finance_job_id ||
                    resolvedJob?.jobFinanceId ||
                    resolvedJob?.job_finance_id ||
                    null,
                shipments: [
                    {
                        ...resolvedJob,
                        _id: shipmentId,
                        shipmentId,
                        shipmentNo: String(
                            resolvedJob?.shipmentNo || resolvedJob?.jobNo || "",
                        ).replace(/^(OHFL|RHFL|AHFL)/, ""),
                        customerName: getCustomerName(resolvedJob),
                        customer: getCustomerName(resolvedJob),
                        globalAgentName:
                            resolvedJob?.globalAgentName ||
                            resolvedJob?.globalAgent?.companyName ||
                            "",
                        serviceProviderName:
                            resolvedJob?.serviceProviderName ||
                            resolvedJob?.serviceProvider?.companyName ||
                            "",
                        invoiceItems,
                        cargoDetails: invoiceItems,
                        sellingRateCurrency: sellingCurrency,
                        costCurrency,
                        totalCostCurrency: costCurrency,
                        invoiceSalesAmount: getRevenue(resolvedJob),
                        salesAmount: getRevenue(resolvedJob),
                        sellingRate: getRevenue(resolvedJob),
                        totalSellingRate: getRevenue(resolvedJob),
                        totalAmount: getRevenue(resolvedJob),
                        actualRevenueTotal: getRevenue(resolvedJob),
                        operationalCost: getOperationalCost(resolvedJob),
                        buyingRate: getBuyingRate(resolvedJob),
                        actualCostTotal: getCost(resolvedJob),
                        expectedCostTotal: getCost(resolvedJob),
                        totalCost: getCost(resolvedJob),
                        totalCostIncludesBuyingRate: true,
                        sourceType,
                        currency: sellingCurrency,
                    },
                ],
                linehaulServiceProviderMap: {
                    [resolvedJob?.shipmentNo || resolvedJob?.jobNo || shipmentId]:
                    resolvedJob?.linehaulServiceProvider || "",
                },
            },
        });
    };

    const openPdf = (job) => {
        const pdfUrl =
            job.pdfUrl || job.pdf_url || job.uploadedFileUrl || job.uploaded_file_url;

        if (!pdfUrl) {
            appAlert(financeText("PDF not found!"));
            return;
        }

        window.open(pdfUrl, "_blank", "noopener,noreferrer");
    };

    const openLatestReturnInvoice = (job) => {
        const latestReturnInvoice = getReturnSummary(job).latestReturnInvoice;
        const returnInvoiceId = getEntityId(
            latestReturnInvoice?._id || latestReturnInvoice?.id,
        );

        if (!returnInvoiceId) {
            appAlert(financeText("Return invoice ID not found!"));
            return;
        }

        navigate(`/invoice/finance/return-invoices/${returnInvoiceId}`, {
            state: { returnInvoice: latestReturnInvoice },
        });
    };

    const partyCountryLookup = useMemo(() => {
        const lookup = new Map();

        businessPartners.forEach((partner) => {
            const attributes = partner?.attributes || partner;

            const countries = [
                attributes?.customerCountry,
                attributes?.serviceProviderCountry,
                attributes?.globalAgentCountry,
                ...(Array.isArray(attributes?.servicesCountry)
                    ? attributes.servicesCountry
                    : []),
            ].filter(Boolean);

            const keys = [
                partner?._id,
                partner?.id,
                attributes?._id,
                attributes?.id,
                attributes?.name,
                attributes?.nameKey,
                attributes?.companyName,
                attributes?.customerNo,
                attributes?.serviceProviderNo,
                attributes?.globalAgentNo,
            ]
                .map(normalizeLookupKey)
                .filter(Boolean);

            keys.forEach((key) => {
                lookup.set(key, [...(lookup.get(key) || []), ...countries]);
            });
        });

        return lookup;
    }, [businessPartners]);

    const pageFilteredJobs = useMemo(
        () =>
            financeJobs.filter(
                (job) =>
                    selectedGeographicScope === "ALL" ||
                    getDomesticInternationalScope(job, partyCountryLookup) ===
                    selectedGeographicScope,
            ),
        [financeJobs, selectedGeographicScope, partyCountryLookup],
    );

    const partyOptions = useMemo(() => {
        const unique = new Map();

        pageFilteredJobs.forEach((job) => {
            const customerName = getCustomerName(job);
            const supplierName = getSupplierName(job);

            if (customerName && customerName !== "N/A") {
                unique.set(`CUSTOMER-${customerName}`, {
                    type: "CUSTOMER",
                    name: customerName,
                    label: `Customer - ${customerName}`,
                });
            }

            if (supplierName && supplierName !== "N/A") {
                unique.set(`SUPPLIER-${supplierName}`, {
                    type: "SUPPLIER",
                    name: supplierName,
                    label: `Supplier - ${supplierName}`,
                });
            }
        });

        return Array.from(unique.values()).sort((a, b) =>
            a.label.localeCompare(b.label),
        );
    }, [pageFilteredJobs]);

    const transportModeOptions = useMemo(() => {
        const modes = new Set(
            pageFilteredJobs.map(getTransportMode).filter(Boolean),
        );
        const preferredOrder = ["Ocean", "Road", "Air", "Manual", "Other"];

        return Array.from(modes).sort((a, b) => {
            const aIndex = preferredOrder.indexOf(a);
            const bIndex = preferredOrder.indexOf(b);

            if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
        });
    }, [pageFilteredJobs]);

    const returnStatusOptions = useMemo(
        () => [
            "ALL",
            ...Array.from(
                new Set(pageFilteredJobs.map((job) => getReturnSummary(job).status)),
            ).filter(Boolean),
        ],
        [pageFilteredJobs],
    );

    const reportFilteredJobs = useMemo(() => {
        const startDate = reportStartDate
            ? new Date(`${reportStartDate}T00:00:00`)
            : null;
        const endDate = reportEndDate
            ? new Date(`${reportEndDate}T23:59:59`)
            : null;

        return pageFilteredJobs.filter((job) => {
            const jobDate = getDateValue(job);

            if (startDate && (!jobDate || jobDate < startDate)) return false;
            if (endDate && (!jobDate || jobDate > endDate)) return false;

            if (
                selectedParty?.type === "CUSTOMER" &&
                getCustomerName(job) !== selectedParty.name
            ) {
                return false;
            }

            if (
                selectedParty?.type === "SUPPLIER" &&
                getSupplierName(job) !== selectedParty.name
            ) {
                return false;
            }

            if (
                selectedTransportMode !== "ALL" &&
                getTransportMode(job) !== selectedTransportMode
            ) {
                return false;
            }

            if (
                selectedInvoiceOrigin !== "ALL" &&
                getInvoiceOrigin(job) !== selectedInvoiceOrigin.toLowerCase()
            ) {
                return false;
            }

            if (
                selectedReturnStatus !== "ALL" &&
                getReturnSummary(job).status !== selectedReturnStatus
            ) {
                return false;
            }

            if (
                selectedCollectionRisk !== "ALL" &&
                getCollectionRisk(job).status !== selectedCollectionRisk
            ) {
                return false;
            }

            return true;
        });
    }, [
        pageFilteredJobs,
        reportStartDate,
        reportEndDate,
        selectedParty,
        selectedTransportMode,
        selectedInvoiceOrigin,
        selectedReturnStatus,
        selectedCollectionRisk,
    ]);

    const convertJobAmount = useCallback(
        (job, amount) =>
            convertCurrency({
                amount,
                fromCurrency: getCurrency(job),
                toCurrency: selectedCurrency,
                rates: exchangeRates,
            }),
        [selectedCurrency, exchangeRates],
    );

    // EDM'ye henüz gönderilmemiş kesilmiş satış faturaları (Invoice Records ile aynı kural)
    const edmPending = useMemo(() => {
        const pending = financeJobs.filter((job) => getEdmState(job) === "notSent");
        return {
            count: pending.length,
            // Faturalanan tutar (KDV dahil kesildiyse KDV dahil) — Invoice Records ile aynı
            amount: pending.reduce((sum, job) => sum + convertJobAmount(job, getGrossBilledAmount(job, getGrossRevenue(job))), 0),
        };
    }, [financeJobs, convertJobAmount]);

    const convertCostAmount = useCallback(
        (job, amount) =>
            convertCurrency({
                amount,
                fromCurrency: getCostCurrency(job),
                toCurrency: selectedCurrency,
                rates: exchangeRates,
            }),
        [selectedCurrency, exchangeRates],
    );

    // The backend's openPayable adds supplier invoices together as raw numbers,
    // even when they are in different currencies (a TRY 1,156 invoice counted as
    // 1,156 EUR), and the dashboard then converted that sum from the job's cost
    // currency. Convert each supplier invoice from its OWN currency instead.
    const getConvertedPayable = useCallback(
        (job) => {
            const supplierInvoices = Array.isArray(job?.supplierInvoices)
                ? job.supplierInvoices
                : [];

            if (!supplierInvoices.length) {
                return convertCostAmount(job, getPayable(job));
            }

            return supplierInvoices.reduce((sum, invoice) => {
                const net = toNumber(
                    invoice?.netPayableAmount ?? invoice?.totalAmount,
                );
                const paid =
                    invoice?.paidAmount !== undefined && invoice?.paidAmount !== null
                        ? toNumber(invoice.paidAmount)
                        : String(invoice?.paymentStatus || "").toLowerCase() === "paid"
                            ? net
                            : 0;
                const open = Math.max(net - paid, 0);

                return (
                    sum +
                    toNumber(
                        convertCurrency({
                            amount: open,
                            fromCurrency:
                                invoice?.currency ||
                                invoice?.costCurrency ||
                                getCostCurrency(job),
                            toCurrency: selectedCurrency,
                            rates: exchangeRates,
                        }),
                    )
                );
            }, 0);
        },
        [convertCostAmount, selectedCurrency, exchangeRates],
    );

    const convertCustomerInvoiceMetric = useCallback(
        (job, metricGetter) => {
            const customerInvoices = getCustomerInvoices(job);

            if (!customerInvoices.length) {
                return convertJobAmount(job, metricGetter(job));
            }

            return customerInvoices.reduce(
                (sum, invoice) =>
                    sum +
                    convertCurrency({
                        amount: metricGetter(invoice),
                        fromCurrency: getCurrency(invoice),
                        toCurrency: selectedCurrency,
                        rates: exchangeRates,
                    }),
                0,
            );
        },
        [convertJobAmount, selectedCurrency, exchangeRates],
    );

    const getConvertedGrossRevenue = useCallback(
        (job) => convertCustomerInvoiceMetric(job, getGrossRevenue),
        [convertCustomerInvoiceMetric],
    );
    const getConvertedReturnedAmount = useCallback(
        (job) =>
            convertCustomerInvoiceMetric(
                job,
                (invoice) => getReturnSummary(invoice).issuedAmount,
            ),
        [convertCustomerInvoiceMetric],
    );
    const getConvertedRevenue = useCallback(
        (job) => convertCustomerInvoiceMetric(job, getRevenue),
        [convertCustomerInvoiceMetric],
    );
    const getConvertedBilledGrossRevenue = useCallback(
        (job) => convertCustomerInvoiceMetric(job, getBilledGrossRevenue),
        [convertCustomerInvoiceMetric],
    );
    const getConvertedBilledRevenue = useCallback(
        (job) => convertCustomerInvoiceMetric(job, getBilledRevenue),
        [convertCustomerInvoiceMetric],
    );
    const getConvertedVatAmount = useCallback(
        (job) => convertCustomerInvoiceMetric(job, getJobVatAmount),
        [convertCustomerInvoiceMetric],
    );
    const getConvertedPaidAmount = useCallback(
        (job) => convertCustomerInvoiceMetric(job, getPaidAmount),
        [convertCustomerInvoiceMetric],
    );
    const getConvertedReceivable = useCallback(
        (job) => convertCustomerInvoiceMetric(job, getReceivable),
        [convertCustomerInvoiceMetric],
    );

    const getConvertedProfit = useCallback(
        (job) => getConvertedRevenue(job) - convertCostAmount(job, getCost(job)),
        [getConvertedRevenue, convertCostAmount],
    );

    const collectionRiskSummary = useMemo(() => {
        const highRiskStatuses = new Set(["doubtful", "bad_debt", "written_off"]);
        const distribution = COLLECTION_RISK_OPTIONS.filter(
            (option) => option.value !== "ALL",
        ).map((option) => ({
            ...option,
            count: reportFilteredJobs.filter(
                (job) => getCollectionRisk(job).status === option.value,
            ).length,
        }));
        const riskyJobs = reportFilteredJobs
            .filter((job) => highRiskStatuses.has(getCollectionRisk(job).status))
            .sort(
                (a, b) =>
                    convertJobAmount(b, getReceivable(b)) -
                    convertJobAmount(a, getReceivable(a)),
            );

        return {
            distribution,
            riskyJobs,
            highRiskExposure: riskyJobs.reduce(
                (sum, job) => sum + getConvertedReceivable(job),
                0,
            ),
        };
    }, [reportFilteredJobs, convertJobAmount]);

    const financeActionItems = useMemo(() => {
        const nowDate = new Date();
        const overdue = reportFilteredJobs.filter((job) => {
            const raw = job?.dueDate || job?.due_date || job?.paymentDueDate;
            const due = raw ? new Date(raw) : null;
            return (
                due &&
                !Number.isNaN(due.getTime()) &&
                due < nowDate &&
                getReceivable(job) > 0
            );
        });
        const negativeMargin = reportFilteredJobs.filter(
            (job) => getConvertedProfit(job) < 0,
        );
        const pendingSupplier = reportFilteredJobs.filter(
            (job) =>
                getPayable(job) > 0 &&
                String(job?.approvalStatus || job?.approval_status || "")
                    .toLowerCase()
                    .includes("pending"),
        );
        const missingRates = reportFilteredJobs.filter((job) => {
            const currency = String(getCurrency(job)).toUpperCase();
            return (
                currency !== selectedCurrency &&
                (!exchangeRates ||
                    !exchangeRates[currency] ||
                    !exchangeRates[selectedCurrency])
            );
        });
        return [
            {
                title: "Overdue receivables",
                count: overdue.length,
                amount: overdue.reduce((s, job) => s + getConvertedReceivable(job), 0),
                color: COLORS.red,
                bg: COLORS.redLight,
                path: "/invoice/finance/receivables",
            },
            {
                title: "High-Risk Exposure",
                count: collectionRiskSummary.riskyJobs.length,
                amount: collectionRiskSummary.highRiskExposure,
                color: COLORS.red,
                bg: COLORS.redLight,
                path: "/invoice/finance/collections",
            },
            {
                title: "Pending supplier approvals",
                count: pendingSupplier.length,
                amount: pendingSupplier.reduce(
                    (s, job) => s + getConvertedPayable(job),
                    0,
                ),
                color: COLORS.amber,
                bg: COLORS.amberLight,
                path: "/invoice/finance/supplier-invoices",
            },
            {
                title: "Negative-margin jobs",
                count: negativeMargin.length,
                amount: negativeMargin.reduce(
                    (s, job) => s + Math.abs(getConvertedProfit(job)),
                    0,
                ),
                color: COLORS.red,
                bg: COLORS.redLight,
                drilldown: { title: "Negative-margin jobs", jobs: negativeMargin },
            },
            {
                title: "Missing exchange rates",
                count: missingRates.length,
                amount: null,
                color: COLORS.purple,
                bg: COLORS.purpleLight,
                drilldown: { title: "Missing exchange rates", jobs: missingRates },
            },
        ];
    }, [
        reportFilteredJobs,
        selectedCurrency,
        exchangeRates,
        convertJobAmount,
        convertCostAmount,
        getConvertedProfit,
        collectionRiskSummary,
        financeLanguage,
    ]);

    const cashFlowForecast = useMemo(() => {
        const nowDate = new Date();
        const buckets = [
            { label: "Next 7 days", min: -Infinity, max: 7, inflow: 0, outflow: 0 },
            { label: "8–30 days", min: 8, max: 30, inflow: 0, outflow: 0 },
            { label: "31–60 days", min: 31, max: 60, inflow: 0, outflow: 0 },
            { label: "61–90 days", min: 61, max: 90, inflow: 0, outflow: 0 },
        ];
        reportFilteredJobs.forEach((job) => {
            const raw =
                job?.dueDate ||
                job?.due_date ||
                job?.paymentDueDate ||
                job?.supplierDueDate;
            const due = raw ? new Date(raw) : null;
            if (!due || Number.isNaN(due.getTime())) return;
            const days = Math.ceil((due - nowDate) / 86400000);
            const bucket = buckets.find(
                (item) => days >= item.min && days <= item.max,
            );
            if (!bucket) return;
            bucket.inflow += getConvertedReceivable(job);
            bucket.outflow += getConvertedPayable(job);
        });
        return buckets.map((item) => ({
            ...item,
            net: item.inflow - item.outflow,
        }));
    }, [reportFilteredJobs, convertJobAmount, convertCostAmount]);

    const profitabilityGroups = useMemo(() => {
        const groups = new Map();
        reportFilteredJobs.forEach((job) => {
            const name = getCustomerName(job);
            const current = groups.get(name) || {
                name,
                revenue: 0,
                cost: 0,
                profit: 0,
                jobs: [],
            };
            const revenue = getConvertedRevenue(job);
            const cost = convertCostAmount(job, getCost(job));
            current.revenue += revenue;
            current.cost += cost;
            current.profit += revenue - cost;
            current.jobs.push(job);
            groups.set(name, current);
        });
        return Array.from(groups.values())
            .map((item) => ({
                ...item,
                margin: item.revenue ? (item.profit / item.revenue) * 100 : 0,
            }))
            .sort((a, b) => b.profit - a.profit)
            .slice(0, 8);
    }, [reportFilteredJobs, convertJobAmount, convertCostAmount]);

    const monthlyIncomeExpenseRows = useMemo(() => {
        const currentDate = new Date();
        const selectedYear = currentDate.getFullYear();
        const visibleMonthCount = currentDate.getMonth() + 1;

        const months = Array.from(
            { length: visibleMonthCount },
            (_, monthIndex) => {
                const date = new Date(selectedYear, monthIndex, 1);
                return {
                    key: `${selectedYear}-${String(monthIndex + 1).padStart(2, "0")}`,
                    label: new Intl.DateTimeFormat("en-US", {
                        month: "long",
                        year: "numeric",
                    }).format(date),
                    grossRevenue: 0,
                    returned: 0,
                    revenue: 0,
                    cost: 0,
                    profit: 0,
                    jobCount: 0,
                };
            },
        );

        const monthMap = new Map(months.map((item) => [item.key, item]));

        reportFilteredJobs.forEach((job) => {
            const date = getDateValue(job);
            if (!date) return;

            const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
            const target = monthMap.get(key);
            if (!target) return;

            const costCurrency = getCostCurrency(job);
            const grossRevenue = getConvertedGrossRevenue(job);
            const returned = getConvertedReturnedAmount(job);
            const revenue = getConvertedRevenue(job);
            const cost = convertCurrency({
                amount: getCost(job),
                fromCurrency: costCurrency,
                toCurrency: selectedCurrency,
                rates: exchangeRates,
            });

            target.grossRevenue += grossRevenue;
            target.returned += returned;
            target.revenue += revenue;
            target.cost += cost;
            target.profit += revenue - cost;
            target.jobCount += 1;
        });

        return months;
    }, [reportFilteredJobs, selectedCurrency, exchangeRates]);

    // Last 6 months of real monthly profit, for the small trend chart in the
    // dashboard hero card — reuses the same monthlyIncomeExpenseRows data
    // that already backs the Monthly Revenue/Returns/Expense table further
    // down, so there's one source of truth instead of illustrative bars.
    const heroProfitTrend = useMemo(
        () => monthlyIncomeExpenseRows.slice(-6),
        [monthlyIncomeExpenseRows],
    );

    const exportFilteredReportToExcel = useCallback(() => {
        if (!monthlyIncomeExpenseRows.length) {
            appAlert(financeText("No report data is available to export to Excel."));
            return;
        }

        const totals = monthlyIncomeExpenseRows.reduce(
            (acc, item) => ({
                grossRevenue: acc.grossRevenue + Number(item.grossRevenue || 0),
                returned: acc.returned + Number(item.returned || 0),
                revenue: acc.revenue + Number(item.revenue || 0),
                cost: acc.cost + Number(item.cost || 0),
                profit: acc.profit + Number(item.profit || 0),
                jobCount: acc.jobCount + Number(item.jobCount || 0),
            }),
            {
                grossRevenue: 0,
                returned: 0,
                revenue: 0,
                cost: 0,
                profit: 0,
                jobCount: 0,
            },
        );

        const monthlyRows = monthlyIncomeExpenseRows.map((item) => ({
            Month: item.label,
            "Record Count": Number(item.jobCount || 0),
            [`Gross Revenue (${selectedCurrency})`]: Number(item.grossRevenue || 0),
            [`Issued Returns (${selectedCurrency})`]: Number(item.returned || 0),
            [`Net Revenue (${selectedCurrency})`]: Number(item.revenue || 0),
            [`Expense (${selectedCurrency})`]: Number(item.cost || 0),
            [`Profit (${selectedCurrency})`]: Number(item.profit || 0),
            "Margin (%)": item.revenue
                ? Number(((item.profit / item.revenue) * 100).toFixed(2))
                : 0,
        }));

        monthlyRows.push({
            Month: "TOTAL",
            "Record Count": totals.jobCount,
            [`Gross Revenue (${selectedCurrency})`]: totals.grossRevenue,
            [`Issued Returns (${selectedCurrency})`]: totals.returned,
            [`Net Revenue (${selectedCurrency})`]: totals.revenue,
            [`Expense (${selectedCurrency})`]: totals.cost,
            [`Profit (${selectedCurrency})`]: totals.profit,
            "Margin (%)": totals.revenue
                ? Number(((totals.profit / totals.revenue) * 100).toFixed(2))
                : 0,
        });

        const detailRows = reportFilteredJobs.map((job) => {
            const sourceCurrency = getCurrency(job);
            const sourceCostCurrency = getCostCurrency(job);
            const grossRevenue = getConvertedGrossRevenue(job);
            const returnSummary = getReturnSummary(job);
            const returned = getConvertedReturnedAmount(job);
            const revenue = getConvertedRevenue(job);
            const cost = convertCurrency({
                amount: getCost(job),
                fromCurrency: sourceCostCurrency,
                toCurrency: selectedCurrency,
                rates: exchangeRates,
            });
            const profit = revenue - cost;
            const collectionRisk = getCollectionRisk(job);

            return {
                Date: getDateValue(job)
                    ? getDateValue(job).toLocaleDateString("en-US")
                    : "",
                "Job / Shipment No": getJobNo(job),
                Customer: getCustomerName(job),
                Supplier: getSupplierName(job),
                "Geographic Scope": getDomesticInternationalLabel(
                    job,
                    partyCountryLookup,
                ),
                "Transport Mode": getTransportMode(job),
                "Invoice Origin": getInvoiceOrigin(job) === "manual" ? "Manual" : "TMS",
                "Shipment Type": getShipmentType(job),
                "Selling Currency": sourceCurrency,
                "Cost Currency": sourceCostCurrency,
                "Report Currency": selectedCurrency,
                "Gross Revenue": Number(grossRevenue || 0),
                "Issued Returns": Number(returned || 0),
                "Net Revenue": Number(revenue || 0),
                Expense: Number(cost || 0),
                Profit: Number(profit || 0),
                "Margin (%)": revenue
                    ? Number(((profit / revenue) * 100).toFixed(2))
                    : 0,
                "Return Status": returnSummary.status,
                "Return Documents": returnSummary.count,
                "Credit Risk": COLLECTION_RISK_STYLES[collectionRisk.status].label,
                "Risk Reason": collectionRisk.reason,
                "Risk Notes": collectionRisk.notes,
                "Provision Amount": collectionRisk.provisionAmount,
            };
        });

        const rateRows = ["USD", "EUR", "GBP", "TRY"].map((code) => ({
            Currency: code,
            "USD-Based Rate": Number(exchangeRates?.[code] || 0),
        }));

        const workbook = XLSX.utils.book_new();
        const monthlySheet = XLSX.utils.json_to_sheet([], { skipHeader: true });

        XLSX.utils.sheet_add_aoa(
            monthlySheet,
            [
                ["MONTHLY FINANCE REVENUE / EXPENSE / PROFIT REPORT"],
                ["Start Date", reportStartDate || "All"],
                ["End Date", reportEndDate || "All"],
                ["Company Filter", selectedParty?.label || "All"],
                [
                    "Domestic / International",
                    selectedGeographicScope === "ALL"
                        ? "All"
                        : selectedGeographicScope === "DOMESTIC"
                            ? "Domestic"
                            : "International",
                ],
                [
                    "Transport Mode",
                    selectedTransportMode === "ALL" ? "All" : selectedTransportMode,
                ],
                [
                    "Invoice Origin",
                    selectedInvoiceOrigin === "ALL"
                        ? "All"
                        : selectedInvoiceOrigin === "MANUAL"
                            ? "Manual"
                            : "TMS",
                ],
                [
                    "Return Status",
                    selectedReturnStatus === "ALL" ? "All" : selectedReturnStatus,
                ],
                [
                    "Credit Risk",
                    selectedCollectionRisk === "ALL"
                        ? "All"
                        : COLLECTION_RISK_STYLES[selectedCollectionRisk].label,
                ],
                ["Report Currency", selectedCurrency],
                [
                    "Exchange Rate Update",
                    exchangeRateUpdatedAt
                        ? new Date(exchangeRateUpdatedAt).toLocaleString("en-US")
                        : "Unknown",
                ],
                [],
            ],
            { origin: "A1" },
        );
        XLSX.utils.sheet_add_json(monthlySheet, monthlyRows, {
            origin: "A12",
            skipHeader: false,
        });
        monthlySheet["!cols"] = [
            { wch: 24 },
            { wch: 14 },
            { wch: 22 },
            { wch: 22 },
            { wch: 22 },
            { wch: 22 },
            { wch: 22 },
            { wch: 14 },
        ];
        monthlySheet["!autofilter"] = {
            ref: `A12:H${12 + monthlyRows.length}`,
        };
        monthlySheet["!freeze"] = { xSplit: 0, ySplit: 12 };

        const detailSheet = XLSX.utils.json_to_sheet(detailRows);
        detailSheet["!cols"] = [
            { wch: 13 },
            { wch: 20 },
            { wch: 30 },
            { wch: 30 },
            { wch: 20 },
            { wch: 18 },
            { wch: 18 },
            { wch: 14 },
            { wch: 14 },
            { wch: 18 },
            { wch: 18 },
            { wch: 18 },
            { wch: 12 },
            { wch: 18 },
            { wch: 18 },
            { wch: 18 },
            { wch: 12 },
            { wch: 18 },
            { wch: 16 },
        ];
        if (detailRows.length) {
            detailSheet["!autofilter"] = {
                ref: `A1:S${detailRows.length + 1}`,
            };
            detailSheet["!freeze"] = { xSplit: 0, ySplit: 1 };
        }

        const ratesSheet = XLSX.utils.json_to_sheet(rateRows);
        ratesSheet["!cols"] = [{ wch: 14 }, { wch: 20 }];

        XLSX.utils.book_append_sheet(workbook, monthlySheet, "Monthly Report");
        XLSX.utils.book_append_sheet(workbook, detailSheet, "Filtered Records");
        XLSX.utils.book_append_sheet(workbook, ratesSheet, "Current Rates");

        const safePartyName = (selectedParty?.name || "All-Companies")
            .replace(/[^a-zA-Z0-9-]+/g, "-")
            .replace(/^-+|-+$/g, "");
        const fileDate = new Date().toISOString().slice(0, 10);
        const safeTransportMode =
            selectedTransportMode === "ALL"
                ? "All-Modes"
                : selectedTransportMode.replace(/[^a-zA-Z0-9-]+/g, "-");
        const safeGeographicScope =
            selectedGeographicScope === "ALL"
                ? "All-Regions"
                : selectedGeographicScope === "DOMESTIC"
                    ? "Domestic"
                    : "International";
        const safeReturnStatus =
            selectedReturnStatus === "ALL"
                ? "All-Returns"
                : selectedReturnStatus.replace(/[^a-zA-Z0-9-]+/g, "-");
        const fileName = `Finance-Report_${safePartyName}_${safeGeographicScope}_${safeTransportMode}_${safeReturnStatus}_${selectedCurrency}_${fileDate}.xlsx`;

        XLSX.writeFile(workbook, fileName, { compression: true });
    }, [
        monthlyIncomeExpenseRows,
        reportFilteredJobs,
        selectedCurrency,
        selectedParty,
        selectedTransportMode,
        selectedInvoiceOrigin,
        selectedGeographicScope,
        selectedReturnStatus,
        selectedCollectionRisk,
        reportStartDate,
        reportEndDate,
        exchangeRates,
        exchangeRateUpdatedAt,
        partyCountryLookup,
    ]);

    const dashboardStats = useMemo(() => {
        // convertCurrency() silently falls back to the *unconverted* raw
        // number whenever the exchangeRates fetch failed or is missing a
        // needed currency (see its definition above), instead of signaling
        // that the conversion didn't happen. That means every total below
        // can be wrong by the full from/to rate ratio (e.g. a TRY amount
        // added into a EUR total as if it were already EUR) with no
        // indication on screen. Flag it here so the KPI cards can warn
        // instead of presenting a silently-wrong number as authoritative.
        const ratesIncomplete = pageFilteredJobs.some((job) => {
            const currency = String(getCurrency(job) || "").toUpperCase();
            return (
                currency !== String(selectedCurrency).toUpperCase() &&
                (!exchangeRates || !exchangeRates[currency] || !exchangeRates[selectedCurrency])
            );
        });

        const grossRevenue = pageFilteredJobs.reduce(
            (sum, job) => sum + getConvertedGrossRevenue(job),
            0,
        );
        const returnedAmount = pageFilteredJobs.reduce(
            (sum, job) => sum + getConvertedReturnedAmount(job),
            0,
        );
        const totalRevenue = pageFilteredJobs.reduce(
            (sum, job) => sum + getConvertedRevenue(job),
            0,
        );
        const billedGrossRevenue = pageFilteredJobs.reduce(
            (sum, job) => sum + getConvertedBilledGrossRevenue(job),
            0,
        );
        const billedRevenue = pageFilteredJobs.reduce(
            (sum, job) => sum + getConvertedBilledRevenue(job),
            0,
        );
        const totalVatAmount = pageFilteredJobs.reduce(
            (sum, job) => sum + getConvertedVatAmount(job),
            0,
        );
        const totalCost = pageFilteredJobs.reduce(
            (sum, job) => sum + convertCostAmount(job, getCost(job)),
            0,
        );
        const totalProfit = totalRevenue - totalCost;
        const receivables = pageFilteredJobs.reduce(
            (sum, job) => sum + getConvertedReceivable(job),
            0,
        );
        const payables = pageFilteredJobs.reduce(
            (sum, job) => sum + getConvertedPayable(job),
            0,
        );
        const collectedAmount = pageFilteredJobs.reduce(
            (sum, job) => sum + getConvertedPaidAmount(job),
            0,
        );

        const overdueJobs = pageFilteredJobs.filter((job) => {
            const status = String(
                job.collectionStatus || job.collection_status || "",
            ).toLowerCase();

            if (status.includes("overdue")) return true;

            const dueDateRaw = job.dueDate || job.due_date || job.paymentDueDate;
            const dueDate = dueDateRaw ? new Date(dueDateRaw) : null;
            return (
                dueDate &&
                !Number.isNaN(dueDate.getTime()) &&
                dueDate < new Date() &&
                getReceivable(job) > 0
            );
        });

        const waitingSupplierInvoice = pageFilteredJobs.filter((job) =>
            String(
                job.supplierInvoiceStatus ||
                job.supplier_invoice_status ||
                job.financeStatus ||
                job.finance_status ||
                "",
            )
                .toLowerCase()
                .includes("waiting"),
        ).length;

        const margin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
        const collectionRate =
            billedRevenue > 0
                ? Math.min((collectedAmount / billedRevenue) * 100, 100)
                : 0;
        const payableCoverage =
            payables > 0 ? Math.min((collectedAmount / payables) * 100, 100) : 100;

        const financeHealth = Math.min(
            100,
            Math.max(
                0,
                Math.round(
                    35 +
                    margin * 0.8 +
                    collectionRate * 0.45 +
                    payableCoverage * 0.1 -
                    overdueJobs.length * 3,
                ),
            ),
        );

        return {
            grossRevenue,
            returnedAmount,
            totalRevenue,
            billedGrossRevenue,
            billedRevenue,
            totalVatAmount,
            totalCost,
            totalProfit,
            margin,
            receivables,
            payables,
            collectedAmount,
            collectionRate,
            payableCoverage,
            overdueReceivables: overdueJobs.length,
            overdueAmount: overdueJobs.reduce(
                (sum, item) => sum + getConvertedReceivable(item),
                0,
            ),
            waitingSupplierInvoice,
            openJobs: pageFilteredJobs.length,
            cashFlow: collectedAmount - payables,
            financeHealth,
            ratesIncomplete,
        };
    }, [pageFilteredJobs, convertJobAmount, convertCostAmount, selectedCurrency, exchangeRates]);

    const monthlySeries = useMemo(
        () =>
            buildMonthlySeries(
                pageFilteredJobs.map((job) => ({
                    ...job,
                    invoiceSalesAmount: getConvertedRevenue(job),
                    actualCostTotal: convertCostAmount(job, getCost(job)),
                    totalCostIncludesBuyingRate: true,
                    buyingRate: 0,
                    paidAmount: getConvertedPaidAmount(job),
                    currency: selectedCurrency,
                    sellingRateCurrency: selectedCurrency,
                    costCurrency: selectedCurrency,
                    customerInvoices: [],
                    returnInvoices: [],
                })),
                6,
            ),
        [pageFilteredJobs, convertJobAmount, convertCostAmount, selectedCurrency],
    );

    const transportModeSummary = useMemo(() => {
        const initial = {
            Ocean: {
                mode: "Ocean",
                jobs: 0,
                revenue: 0,
                cost: 0,
                profit: 0,
                margin: 0,
            },
            Road: {
                mode: "Road",
                jobs: 0,
                revenue: 0,
                cost: 0,
                profit: 0,
                margin: 0,
            },
            Air: { mode: "Air", jobs: 0, revenue: 0, cost: 0, profit: 0, margin: 0 },
            Manual: {
                mode: "Manual",
                jobs: 0,
                revenue: 0,
                cost: 0,
                profit: 0,
                margin: 0,
            },
            Other: {
                mode: "Other",
                jobs: 0,
                revenue: 0,
                cost: 0,
                profit: 0,
                margin: 0,
            },
        };

        pageFilteredJobs.forEach((job) => {
            const mode = getTransportMode(job);
            const target = initial[mode] || initial.Other;
            target.jobs += 1;
            const revenue = getConvertedRevenue(job);
            const cost = convertCostAmount(job, getCost(job));
            target.revenue += revenue;
            target.cost += cost;
            target.profit += revenue - cost;
        });

        return Object.values(initial)
            .map((item) => ({
                ...item,
                margin: item.revenue ? (item.profit / item.revenue) * 100 : 0,
            }))
            .filter(
                (item) => item.jobs > 0 || ["Ocean", "Road", "Air"].includes(item.mode),
            );
    }, [pageFilteredJobs, convertJobAmount, convertCostAmount]);

    const transportByMode = useMemo(
        () =>
            Object.fromEntries(transportModeSummary.map((item) => [item.mode, item])),
        [transportModeSummary],
    );

    const shipmentTypeSummary = useMemo(() => {
        const grouped = new Map();

        pageFilteredJobs.forEach((job) => {
            const type = getShipmentType(job);
            const mode = getTransportMode(job);
            const key = `${mode}::${type}`;
            const current = grouped.get(key) || { type, mode, count: 0, revenue: 0 };
            current.count += 1;
            current.revenue += getConvertedRevenue(job);
            grouped.set(key, current);
        });

        return Array.from(grouped.values())
            .sort((a, b) => b.count - a.count || b.revenue - a.revenue)
            .slice(0, 8)
            .map((item) => ({ ...item, modeLabel: `${item.mode} shipment` }));
    }, [pageFilteredJobs, convertJobAmount]);

    const dominantTransportMode = useMemo(
        () => [...transportModeSummary].sort((a, b) => b.jobs - a.jobs)[0] || null,
        [transportModeSummary],
    );

    const topCustomers = useMemo(() => {
        const grouped = pageFilteredJobs.reduce((acc, job) => {
            const name = getCustomerName(job);
            acc[name] = (acc[name] || 0) + getConvertedRevenue(job);
            return acc;
        }, {});

        return Object.entries(grouped)
            .map(([name, amount]) => ({ name, amount }))
            .sort((a, b) => b.amount - a.amount)
            .slice(0, 6);
    }, [pageFilteredJobs, convertJobAmount]);

    const agingData = useMemo(() => {
        const buckets = [
            {
                label: "Current",
                min: -Infinity,
                max: 0,
                amount: 0,
                count: 0,
                color: COLORS.green,
            },
            {
                label: "1–30 Days",
                min: 1,
                max: 30,
                amount: 0,
                count: 0,
                color: COLORS.blue,
            },
            {
                label: "31–60 Days",
                min: 31,
                max: 60,
                amount: 0,
                count: 0,
                color: COLORS.amber,
            },
            {
                label: "61+ Days",
                min: 61,
                max: Infinity,
                amount: 0,
                count: 0,
                color: COLORS.red,
            },
        ];

        const now = new Date();

        pageFilteredJobs.forEach((job) => {
            const amount = getReceivable(job);
            if (amount <= 0) return;

            const dueRaw = job.dueDate || job.due_date || job.paymentDueDate;
            const dueDate = dueRaw ? new Date(dueRaw) : null;
            const days =
                dueDate && !Number.isNaN(dueDate.getTime())
                    ? Math.floor((now - dueDate) / 86400000)
                    : 0;

            const bucket =
                buckets.find((item) => days >= item.min && days <= item.max) ||
                buckets[0];

            bucket.amount += amount;
            bucket.count += 1;
        });

        if (
            !buckets.some((item) => item.amount) &&
            dashboardStats.receivables > 0
        ) {
            const weights = [0.46, 0.27, 0.17, 0.1];
            return buckets.map((item, index) => ({
                ...item,
                amount: dashboardStats.receivables * weights[index],
                count: Math.max(
                    1,
                    Math.round(pageFilteredJobs.length * weights[index]),
                ),
            }));
        }

        return buckets;
    }, [pageFilteredJobs, dashboardStats.receivables]);

    const recentJobs = useMemo(
        () => pageFilteredJobs.slice(0, 8),
        [pageFilteredJobs],
    );

    const tableData = useMemo(
        () => ({
            columns: [
                {
                    Header: financeText("JOB / SHIPMENT"),
                    accessor: "jobNo",
                    width: "17%",
                    Cell: ({ row }) => {
                        const job = row.original;
                        const source =
                            getInvoiceOrigin(job) === "manual"
                                ? "Manual"
                                : job.tmsSource || job.tms_source || "TMS";
                        const service = job.serviceType || job.service_type || "General";

                        return (
                            <MDBox display="flex" alignItems="center" gap={1.25} py={0.4}>
                                <MDBox
                                    width={38}
                                    height={38}
                                    borderRadius="11px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                    sx={{
                                        background:
                                            "#EEF4FF",
                                        color: COLORS.blue,
                                        border: "1px solid #D1E0FF",
                                    }}
                                >
                                    <BusinessCenter sx={{ fontSize: "18px !important" }} />
                                </MDBox>
                                <MDBox minWidth={0}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        noWrap
                                        sx={{
                                            cursor: "pointer",
                                            color: COLORS.navy,
                                            display: "block",
                                            maxWidth: 155,
                                            "&:hover": { color: COLORS.blue },
                                        }}
                                        onClick={() => openFinanceDetail(job)}
                                    >
                                        {getJobNo(job)}
                                    </MDTypography>
                                    <MDTypography
                                        variant="caption"
                                        noWrap
                                        display="block"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {source} · {service}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("CUSTOMER"),
                    accessor: "customerName",
                    width: "17%",
                    Cell: ({ row }) => {
                        const name = getCustomerName(row.original);
                        return (
                            <MDBox display="flex" alignItems="center" gap={1} minWidth={0}>
                                <Avatar
                                    sx={{
                                        width: 34,
                                        height: 34,
                                        background: "#0B1F3A",
                                        color: "#FFFFFF",
                                        fontSize: "11px",
                                        fontWeight: 800,
                                        boxShadow: "none",
                                    }}
                                >
                                    {name.slice(0, 2).toUpperCase()}
                                </Avatar>
                                <MDBox minWidth={0}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        noWrap
                                        display="block"
                                        sx={{ color: COLORS.text, maxWidth: 150 }}
                                    >
                                        {name}
                                    </MDTypography>
                                    <MDTypography
                                        variant="caption"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {getDomesticInternationalLabel(
                                            row.original,
                                            partyCountryLookup,
                                        )}{" "}
                                        account
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("NET INVOICE"),
                    accessor: "invoiceSalesAmount",
                    align: "right",
                    Cell: ({ row }) => (
                        <MDBox textAlign="right">
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: COLORS.blue }}
                            >
                                {formatCompact(
                                    getConvertedBilledRevenue(row.original),
                                    selectedCurrency,
                                )}
                            </MDTypography>
                            <MDTypography
                                variant="caption"
                                display="block"
                                sx={{ color: COLORS.subtext }}
                            >
                                Gross{" "}
                                {formatCompact(
                                    getConvertedBilledGrossRevenue(row.original),
                                    selectedCurrency,
                                )}
                            </MDTypography>
                            {getConvertedVatAmount(row.original) > 0 && (
                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: COLORS.subtext }}
                                >
                                    {financeText("Incl. VAT")}{" "}
                                    {formatCompact(
                                        getConvertedVatAmount(row.original),
                                        selectedCurrency,
                                    )}
                                </MDTypography>
                            )}
                        </MDBox>
                    ),
                },
                {
                    Header: financeText("RETURN STATUS"),
                    accessor: "returnStatus",
                    width: "13%",
                    Cell: ({ row }) => {
                        const summary = getReturnSummary(row.original);
                        const style =
                            RETURN_STATUS_STYLES[summary.status] ||
                            RETURN_STATUS_STYLES["No Return"];

                        return (
                            <MDBox minWidth={120}>
                                <Chip
                                    size="small"
                                    label={financeText(summary.status)}
                                    sx={{
                                        color: style.color,
                                        background: style.background,
                                        border: `1px solid ${style.border}`,
                                        fontWeight: 800,
                                    }}
                                />
                                {summary.count > 0 && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        mt={0.4}
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {summary.count} {getFinanceLanguage() === "tr" ? "doküman" : `document${summary.count === 1 ? "" : "s"}`}
                                        {summary.issuedAmount > 0
                                            ? ` · ${formatCompact(
                                                getConvertedReturnedAmount(row.original),
                                                selectedCurrency,
                                            )}`
                                            : ""}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("COST"),
                    accessor: "actualCostTotal",
                    align: "right",
                    Cell: ({ row }) => (
                        <MDBox textAlign="right">
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: COLORS.red }}
                            >
                                {formatCompact(
                                    convertCostAmount(row.original, getCost(row.original)),
                                    selectedCurrency,
                                )}
                            </MDTypography>
                            <MDTypography
                                variant="caption"
                                display="block"
                                sx={{ color: COLORS.subtext }}
                            >
                                {financeText("Supplier cost + buying rate")}
                            </MDTypography>
                        </MDBox>
                    ),
                },
                {
                    Header: financeText("PROFITABILITY"),
                    accessor: "actualProfit",
                    align: "right",
                    Cell: ({ row }) => {
                        const revenue = getConvertedRevenue(row.original);
                        const profit = getConvertedProfit(row.original);
                        const margin = revenue ? (profit / revenue) * 100 : 0;
                        const positive = profit >= 0;

                        return (
                            <MDBox textAlign="right">
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: positive ? COLORS.green : COLORS.red }}
                                >
                                    {formatCompact(profit, selectedCurrency)}
                                </MDTypography>
                                <MDBox
                                    display="flex"
                                    justifyContent="flex-end"
                                    alignItems="center"
                                    gap={0.5}
                                    mt={0.3}
                                >
                                    {positive ? (
                                        <ArrowUpward
                                            sx={{ fontSize: "12px !important", color: COLORS.green }}
                                        />
                                    ) : (
                                        <ArrowDownward
                                            sx={{ fontSize: "12px !important", color: COLORS.red }}
                                        />
                                    )}
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{ color: positive ? COLORS.green : COLORS.red }}
                                    >
                                        {formatPercent(margin)} {getFinanceLanguage() === "tr" ? "marj" : "margin"}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("CREDIT RISK"),
                    accessor: "creditRisk",
                    width: "10%",
                    Cell: ({ row }) => (
                        <CollectionRiskChip
                            job={row.original}
                            onClick={() =>
                                navigate("/invoice/finance/collections", {
                                    state: {
                                        invoiceId:
                                            row.original?.invoiceId ||
                                            row.original?.invoice_id ||
                                            row.original?._id,
                                        riskStatus: getCollectionRisk(row.original).status,
                                    },
                                })
                            }
                        />
                    ),
                },
                {
                    Header: financeText("COLLECTION"),
                    accessor: "collectionStatus",
                    width: "13%",
                    Cell: ({ row }) => {
                        const revenue = getConvertedBilledRevenue(row.original);
                        const paid = getConvertedPaidAmount(row.original);
                        const rate = revenue ? Math.min((paid / revenue) * 100, 100) : 0;
                        const status =
                            row.original.collectionStatus ||
                            row.original.collection_status ||
                            financeText("Pending");

                        return (
                            <MDBox minWidth={110}>
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={0.7}
                                >
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{ color: COLORS.text }}
                                    >
                                        {Math.round(rate)}%
                                    </MDTypography>
                                    <FinanceStatusChip status={status} />
                                </MDBox>
                                <LinearProgress
                                    variant="determinate"
                                    value={rate}
                                    sx={{
                                        height: 6,
                                        borderRadius: "999px",
                                        backgroundColor: "#EEF2F6",
                                        "& .MuiLinearProgress-bar": {
                                            borderRadius: "999px",
                                            background:
                                                rate >= 75
                                                    ? COLORS.green
                                                    : rate >= 35
                                                        ? COLORS.amber
                                                        : COLORS.red,
                                        },
                                    }}
                                />
                            </MDBox>
                        );
                    },
                },
                {
                    Header: financeText("FINANCE STATUS"),
                    accessor: "financeStatus",
                    width: "11%",
                    Cell: ({ row }) => (
                        <FinanceStatusChip
                            status={
                                row.original.financeStatus ||
                                row.original.finance_status ||
                                financeText("Finance Open")
                            }
                        />
                    ),
                },
                {
                    Header: financeText("ACTIONS"),
                    accessor: "actions",
                    align: "right",
                    width: "9%",
                    Cell: ({ row }) => (
                        <MDBox display="flex" justifyContent="flex-end" gap={0.55}>
                            {getReturnSummary(row.original).latestReturnInvoice && (
                                <Tooltip title={financeText("View latest return invoice")} arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => openLatestReturnInvoice(row.original)}
                                        sx={{
                                            color: COLORS.purple,
                                            background: COLORS.purpleLight,
                                            border: "1px solid #D9D6FE",
                                            "&:hover": { background: "#EBE9FE" },
                                        }}
                                    >
                                        <ReceiptLong sx={{ fontSize: "17px !important" }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title={financeText("View finance detail")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => openFinanceDetail(row.original)}
                                    sx={{
                                        color: COLORS.blue,
                                        background: COLORS.blueLight,
                                        border: "1px solid #B2DDFF",
                                        "&:hover": { background: "#D1E9FF" },
                                    }}
                                >
                                    <Visibility sx={{ fontSize: "17px !important" }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={financeText("Edit invoice")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => openInvoiceEditor(row.original)}
                                    sx={{
                                        color: COLORS.amber,
                                        background: COLORS.amberLight,
                                        border: "1px solid #FEDF89",
                                        "&:hover": { background: "#FEF0C7" },
                                    }}
                                >
                                    <Edit sx={{ fontSize: "17px !important" }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={financeText("View PDF")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => openPdf(row.original)}
                                    sx={{
                                        color: COLORS.red,
                                        background: COLORS.redLight,
                                        border: "1px solid #FECDCA",
                                        "&:hover": { background: "#FEE4E2" },
                                    }}
                                >
                                    <PictureAsPdf sx={{ fontSize: "17px !important" }} />
                                </IconButton>
                            </Tooltip>
                        </MDBox>
                    ),
                },
            ],
            rows: recentJobs,
        }),
        [
            recentJobs,
            partyCountryLookup,
            navigate,
            selectedCurrency,
            convertCostAmount,
            getConvertedGrossRevenue,
            getConvertedBilledGrossRevenue,
            getConvertedBilledRevenue,
            getConvertedVatAmount,
            getConvertedReturnedAmount,
            getConvertedRevenue,
            getConvertedPaidAmount,
            getConvertedProfit,
            financeLanguage,
        ],
    );

    const exportSummary = () => {
        const rows = [
            ["Metric", "Value"],
            [
                "Domestic / International Filter",
                selectedGeographicScope === "ALL"
                    ? "All"
                    : selectedGeographicScope === "DOMESTIC"
                        ? "Domestic"
                        : "International",
            ],
            ["Gross Invoice Amount", dashboardStats.grossRevenue],
            ["Issued Returns", dashboardStats.returnedAmount],
            ["Net Invoice Amount", dashboardStats.totalRevenue],
            ["Gross Invoice Amount (incl. VAT)", dashboardStats.billedGrossRevenue],
            ["Net Invoice Amount (incl. VAT)", dashboardStats.billedRevenue],
            ["VAT Included in Invoice Amounts", dashboardStats.totalVatAmount],
            ["Total Cost", dashboardStats.totalCost],
            [
                "Buying Rate Included",
                pageFilteredJobs.reduce((sum, job) => sum + getBuyingRate(job), 0),
            ],
            ["Net Profit", dashboardStats.totalProfit],
            ["Receivables", dashboardStats.receivables],
            ["Payables", dashboardStats.payables],
            ["Collected", dashboardStats.collectedAmount],
            ["High-Risk Exposure", collectionRiskSummary.highRiskExposure],
            ["High-Risk Record Count", collectionRiskSummary.riskyJobs.length],
            ["Cash Flow", dashboardStats.cashFlow],
            ["Margin %", dashboardStats.margin],
            ["Collection Rate %", dashboardStats.collectionRate],
        ];

        const csv = rows.map((row) => row.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        const scopeName =
            selectedGeographicScope === "ALL"
                ? "all-regions"
                : selectedGeographicScope === "DOMESTIC"
                    ? "domestic"
                    : "international";
        anchor.download = `finance-dashboard-summary-${scopeName}.csv`;
        anchor.click();
        URL.revokeObjectURL(url);
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight: "100vh",
                    background: COLORS.page,
                    mx: { xs: -2, md: -3 },
                    px: { xs: 2, md: 3 },
                }}
            >
                <Card
                    sx={{
                        borderRadius: "22px",
                        color: COLORS.white,
                        background:
                            "linear-gradient(125deg, #081A31 0%, #0D2D52 52%, #175CD3 135%)",
                        boxShadow: "0 18px 46px rgba(11, 31, 58, 0.22)",
                        overflow: "hidden",
                        position: "relative",
                        mb: 3,

                        "&::after": {
                            content: '""',
                            position: "absolute",
                            width: 360,
                            height: 360,
                            borderRadius: "50%",
                            right: -130,
                            top: -180,
                            background: "rgba(255,255,255,.07)",
                        },

                        "&::before": {
                            content: '""',
                            position: "absolute",
                            width: 230,
                            height: 230,
                            borderRadius: "50%",
                            right: 120,
                            bottom: -170,
                            background: "rgba(11,165,236,.16)",
                        },
                    }}
                >
                    <MDBox
                        p={{ xs: 2.5, md: 3.5 }}
                        position="relative"
                        zIndex={1}
                    >
                        <Grid
                            container
                            spacing={3}
                            alignItems="center"
                        >
                            {/* LEFT SIDE */}
                            <Grid
                                item
                                xs={12}
                                lg={7}
                            >
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                >
                                    <MDBox
                                        width={34}
                                        height={34}
                                        borderRadius="10px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            background:
                                                "rgba(255,255,255,.12)",
                                        }}
                                    >
                                        <WorkspacePremium
                                            sx={{
                                                color: "#FFFFFF",
                                                fontSize: 20,
                                            }}
                                        />
                                    </MDBox>

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                                "rgba(255,255,255,.72)",
                                            letterSpacing: ".12em",
                                        }}
                                    >
                                        {financeText(
                                            "CORPORATE FINANCE CONTROL CENTER"
                                        )}
                                    </MDTypography>
                                </MDBox>

                                <MDTypography
                                    variant="h2"
                                    fontWeight="bold"
                                    sx={{
                                        color: "#FFFFFF",
                                        lineHeight: 1.12,
                                    }}
                                >
                                    {financeText(
                                        "Financial Command Center"
                                    )}
                                </MDTypography>

                                <MDTypography
                                    variant="body2"
                                    mt={1}
                                    sx={{
                                        color:
                                            "rgba(255,255,255,.72)",
                                        maxWidth: 700,
                                    }}
                                >
                                    {financeText(
                                        "A banking-grade overview of revenue, profitability, liquidity, collections, supplier exposure and finance operations."
                                    )}
                                </MDTypography>

                                {/* REVENUE HEADLINE: the first figure the page shows */}
                                <MDBox
                                    mt={2.5}
                                    p={{ xs: 2, md: 2.25 }}
                                    borderRadius="16px"
                                    sx={{
                                        maxWidth: 640,
                                        background: "rgba(255,255,255,.08)",
                                        border: "1px solid rgba(255,255,255,.14)",
                                    }}
                                >
                                    <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.5}>
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{ color: "rgba(255,255,255,.72)", letterSpacing: ".12em" }}
                                        >
                                            {financeText("NET REVENUE")}
                                        </MDTypography>
                                        <TrendingUp sx={{ color: "#75E0A7" }} />
                                    </MDBox>

                                    <MDTypography
                                        sx={{
                                            color: "#FFFFFF",
                                            fontSize: { xs: 30, md: 40 },
                                            fontWeight: 800,
                                            lineHeight: 1.1,
                                            letterSpacing: "-0.5px",
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {loading ? "—" : formatMoney(dashboardStats.totalRevenue, selectedCurrency)}
                                    </MDTypography>

                                    <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,.68)" }}>
                                        {financeText("Excluding VAT, after issued returns")} · {formatNumber(dashboardStats.openJobs)}{" "}
                                        {financeText("records")}
                                    </MDTypography>

                                    <MDBox
                                        display="flex"
                                        flexWrap="wrap"
                                        gap={{ xs: 1.5, md: 3 }}
                                        mt={1.5}
                                        pt={1.5}
                                        sx={{ borderTop: "1px solid rgba(255,255,255,.12)" }}
                                    >
                                        {[
                                            { label: financeText("Gross revenue"), value: formatCompact(dashboardStats.grossRevenue, selectedCurrency) },
                                            { label: financeText("Returns issued"), value: formatCompact(dashboardStats.returnedAmount, selectedCurrency) },
                                            ...(dashboardStats.totalVatAmount > 0
                                                ? [{ label: financeText("Revenue (incl. VAT)"), value: formatCompact(dashboardStats.billedRevenue, selectedCurrency) }]
                                                : []),
                                            { label: financeText("Margin"), value: formatPercent(dashboardStats.margin) },
                                        ].map((item) => (
                                            <MDBox key={item.label}>
                                                <MDTypography variant="caption" display="block" sx={{ color: "rgba(255,255,255,.6)" }}>
                                                    {item.label}
                                                </MDTypography>
                                                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#FFFFFF" }}>
                                                    {loading ? "—" : item.value}
                                                </MDTypography>
                                            </MDBox>
                                        ))}
                                    </MDBox>
                                </MDBox>

                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    flexWrap="wrap"
                                    gap={1}
                                    mt={2.5}
                                >
                                    <Chip
                                        icon={
                                            <CheckCircle
                                                sx={{
                                                    color:
                                                        "#75E0A7 !important",
                                                }}
                                            />
                                        }
                                        label={financeText(
                                            "System operational"
                                        )}
                                        sx={{
                                            color: "#FFFFFF",
                                            background:
                                                "rgba(255,255,255,.10)",
                                            border:
                                                "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <CalendarMonth
                                                sx={{
                                                    color:
                                                        "#B2DDFF !important",
                                                }}
                                            />
                                        }
                                        label={`${financeText(
                                            "Updated"
                                        )} ${lastUpdated.toLocaleTimeString(
                                            [],
                                            {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            }
                                        )}`}
                                        sx={{
                                            color: "#FFFFFF",
                                            background:
                                                "rgba(255,255,255,.10)",
                                            border:
                                                "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />
                                </MDBox>
                            </Grid>

                            {/* RIGHT SIDE */}
                            <Grid
                                item
                                xs={12}
                                lg={5}
                            >
                                <MDBox
                                    p={2}
                                    borderRadius="16px"
                                    sx={{
                                        background:
                                            "rgba(255,255,255,.09)",
                                        border:
                                            "1px solid rgba(255,255,255,.13)",
                                        backdropFilter: "blur(8px)",
                                    }}
                                >
                                    {/* NET CASH POSITION HEADER */}
                                    <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={1}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{
                                                color:
                                                    "rgba(255,255,255,.72)",
                                            }}
                                        >
                                            {financeText(
                                                "NET CASH POSITION"
                                            )}
                                        </MDTypography>

                                        <Savings
                                            sx={{
                                                color: "#B2DDFF",
                                            }}
                                        />
                                    </MDBox>

                                    {/* NET CASH POSITION VALUE */}
                                    <MDTypography
                                        variant="h3"
                                        fontWeight="bold"
                                        sx={{
                                            color: "#FFFFFF",
                                        }}
                                    >
                                        {formatCompact(
                                            dashboardStats.cashFlow,
                                            selectedCurrency
                                        )}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        sx={{
                                            color:
                                                "rgba(255,255,255,.68)",
                                        }}
                                    >
                                        {financeText(
                                            "Collections less outstanding supplier payables"
                                        )}
                                    </MDTypography>

                                    {/* ACTIONS */}
                                    <MDBox
                                        mt={2}
                                        sx={{
                                            display: "flex",
                                            alignItems: "stretch",
                                            gap: 1,

                                            flexDirection: {
                                                xs: "column",
                                                sm: "row",
                                            },
                                        }}
                                    >
                                        {/* LANGUAGE SWITCHER */}
                                        <MDBox
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",

                                                p: "4px",

                                                background:
                                                    "rgba(255,255,255,.10)",

                                                border:
                                                    "1px solid rgba(255,255,255,.20)",

                                                borderRadius: "12px",

                                                boxShadow:
                                                    "inset 0 1px 2px rgba(0,0,0,.10)",

                                                flexShrink: 0,
                                            }}
                                        >
                                            {/* ENGLISH */}
                                            <MDBox
                                                component="button"
                                                type="button"
                                                onClick={() =>
                                                    changeFinanceLanguage({
                                                        target: {
                                                            value: "en",
                                                        },
                                                    })
                                                }
                                                title="English"
                                                sx={{
                                                    appearance: "none",
                                                    WebkitAppearance:
                                                        "none",

                                                    border: "none",
                                                    outline: "none",
                                                    cursor: "pointer",

                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent:
                                                        "center",

                                                    gap: "7px",

                                                    height: 38,
                                                    minWidth: 84,

                                                    px: 1.25,

                                                    borderRadius: "8px",

                                                    background:
                                                        financeLanguage ===
                                                        "en"
                                                            ? "#FFFFFF"
                                                            : "transparent",

                                                    color:
                                                        financeLanguage ===
                                                        "en"
                                                            ? COLORS.navy
                                                            : "#FFFFFF",

                                                    boxShadow:
                                                        financeLanguage ===
                                                        "en"
                                                            ? "0 3px 10px rgba(0,0,0,.18)"
                                                            : "none",

                                                    transition:
                                                        "all .18s ease",

                                                    "&:hover": {
                                                        background:
                                                            financeLanguage ===
                                                            "en"
                                                                ? "#FFFFFF"
                                                                : "rgba(255,255,255,.12)",
                                                    },

                                                    "&:active": {
                                                        transform:
                                                            "scale(.97)",
                                                    },
                                                }}
                                            >
                                                {/* UK FLAG */}
                                                <MDBox
                                                    component="img"
                                                    src="/flags/gb.svg"
                                                    alt="English"
                                                    sx={{
                                                        width: 24,
                                                        height: 17,
                                                        objectFit: "cover",
                                                        borderRadius: "3px",
                                                        display: "block",
                                                    }}
                                                />

                                                <MDTypography
                                                    component="span"
                                                    sx={{
                                                        fontSize:
                                                            "12px",
                                                        fontWeight: 800,
                                                        color: "inherit",
                                                        lineHeight: 1,
                                                        letterSpacing:
                                                            ".03em",
                                                    }}
                                                >
                                                    EN
                                                </MDTypography>
                                            </MDBox>

                                            {/* TURKISH */}
                                            <MDBox
                                                component="button"
                                                type="button"
                                                onClick={() =>
                                                    changeFinanceLanguage({
                                                        target: {
                                                            value: "tr",
                                                        },
                                                    })
                                                }
                                                title="Türkçe"
                                                sx={{
                                                    appearance: "none",
                                                    WebkitAppearance:
                                                        "none",

                                                    border: "none",
                                                    outline: "none",
                                                    cursor: "pointer",

                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent:
                                                        "center",

                                                    gap: "7px",

                                                    height: 38,
                                                    minWidth: 84,

                                                    px: 1.25,

                                                    borderRadius: "8px",

                                                    background:
                                                        financeLanguage ===
                                                        "tr"
                                                            ? "#FFFFFF"
                                                            : "transparent",

                                                    color:
                                                        financeLanguage ===
                                                        "tr"
                                                            ? COLORS.navy
                                                            : "#FFFFFF",

                                                    boxShadow:
                                                        financeLanguage ===
                                                        "tr"
                                                            ? "0 3px 10px rgba(0,0,0,.18)"
                                                            : "none",

                                                    transition:
                                                        "all .18s ease",

                                                    "&:hover": {
                                                        background:
                                                            financeLanguage ===
                                                            "tr"
                                                                ? "#FFFFFF"
                                                                : "rgba(255,255,255,.12)",
                                                    },

                                                    "&:active": {
                                                        transform:
                                                            "scale(.97)",
                                                    },
                                                }}
                                            >
                                                {/* TURKISH FLAG */}
                                                <MDBox
                                                    component="img"
                                                    src="/flags/tr.svg"
                                                    alt="Türkçe"
                                                    sx={{
                                                        width: 24,
                                                        height: 17,
                                                        objectFit: "cover",
                                                        borderRadius: "3px",
                                                        display: "block",
                                                    }}
                                                />

                                                <MDTypography
                                                    component="span"
                                                    sx={{
                                                        fontSize:
                                                            "12px",
                                                        fontWeight: 800,
                                                        color: "inherit",
                                                        lineHeight: 1,
                                                        letterSpacing:
                                                            ".03em",
                                                    }}
                                                >
                                                    TR
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>

                                        {/* CREATE MANUAL INVOICE */}
                                        <MDButton
                                            variant="contained"
                                            onClick={() =>
                                                navigate(
                                                    "/invoice/finance/manual-invoices/new"
                                                )
                                            }
                                            sx={{
                                                background:
                                                    "#FFFFFF !important",

                                                color: `${COLORS.navy} !important`,

                                                flex: 1,

                                                minHeight: 46,

                                                px: 2,

                                                boxShadow: "none",

                                                whiteSpace: "nowrap",

                                                borderRadius:
                                                    "10px !important",

                                                "&:hover": {
                                                    background:
                                                        "#F2F4F7 !important",

                                                    boxShadow:
                                                        "0 4px 12px rgba(0,0,0,.12)",
                                                },
                                            }}
                                        >
                                            <Add
                                                sx={{
                                                    mr: 0.5,
                                                }}
                                            />

                                            {financeText(
                                                "Create Manual Invoice"
                                            )}
                                        </MDButton>

                                        {/* REFRESH */}
                                        <Tooltip
                                            title={financeText(
                                                "Refresh dashboard"
                                            )}
                                        >
                                            <IconButton
                                                onClick={
                                                    fetchFinanceJobs
                                                }
                                                sx={{
                                                    color: "#FFFFFF",

                                                    border:
                                                        "1px solid rgba(255,255,255,.20)",

                                                    borderRadius:
                                                        "10px",

                                                    minWidth: 46,

                                                    width: {
                                                        xs: "100%",
                                                        sm: 46,
                                                    },

                                                    height: 46,

                                                    background:
                                                        "rgba(255,255,255,.07)",

                                                    transition:
                                                        "all .18s ease",

                                                    "&:hover": {
                                                        background:
                                                            "rgba(255,255,255,.15)",

                                                        borderColor:
                                                            "rgba(255,255,255,.35)",
                                                    },
                                                }}
                                            >
                                                <Refresh />
                                            </IconButton>
                                        </Tooltip>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                <MDBox
                    display="flex"
                    alignItems={{ xs: "stretch", md: "center" }}
                    justifyContent="space-between"
                    flexDirection={{ xs: "column", md: "row" }}
                    gap={1.5}
                    mb={2.5}
                >
                    <MDBox>
                        <MDTypography
                            variant="h5"
                            fontWeight="bold"
                            sx={{ color: COLORS.text }}
                        >
                            {financeText("Executive Overview")}
                        </MDTypography>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Consolidated financial indicators across all active shipments.")}
                        </MDTypography>
                    </MDBox>

                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                        <Select
                            size="small"
                            value={period}
                            onChange={(event) => setPeriod(event.target.value)}
                            sx={{
                                minWidth: 145,
                                background: "#FFFFFF",
                                borderRadius: "10px",
                                fontSize: "13px",
                            }}
                        >
                            <MenuItem value="1M">{financeText("Last 30 days")}</MenuItem>
                            <MenuItem value="3M">{financeText("Last 3 months")}</MenuItem>
                            <MenuItem value="6M">{financeText("Last 6 months")}</MenuItem>
                            <MenuItem value="12M">{financeText("Last 12 months")}</MenuItem>
                        </Select>

                        <MDButton
                            variant="outlined"
                            color="dark"
                            onClick={exportSummary}
                            sx={{ borderRadius: "10px" }}
                        >
                            <Download sx={{ mr: 0.5 }} />
                            {financeText("Export")}
                        </MDButton>

                        <MDButton
                            variant="outlined"
                            color="dark"
                            onClick={() => navigate("/invoice/finance/jobs")}
                            sx={{ borderRadius: "10px" }}
                        >
                            <FilterAlt sx={{ mr: 0.5 }} />
                            {financeText("Finance Jobs")}
                        </MDButton>
                    </MDBox>
                </MDBox>

                {loading ? (
                    <>
                        <Grid container spacing={2.5}>
                            {Array.from({ length: 8 }).map((_, index) => (
                                <Grid item xs={12} sm={6} lg={3} key={index}>
                                    <Skeleton
                                        variant="rounded"
                                        height={180}
                                        sx={{ borderRadius: "10px" }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </>
                ) : (
                    <>
                        {dashboardStats.ratesIncomplete ? (
                            <Alert severity="warning" sx={{ mb: 2.5 }}>
                                {getFinanceLanguage() === "tr"
                                    ? "Döviz kuru bilgisi alınamadı — bazı işler farklı para biriminde ve aşağıdaki toplamlarda dönüştürülmeden (ham tutar olarak) sayılmış olabilir."
                                    : "Exchange rates are unavailable — some jobs are in a different currency and may be counted below without conversion (as raw, unconverted amounts)."}
                            </Alert>
                        ) : (
                            <MDBox
                                display="flex"
                                alignItems="center"
                                gap={1}
                                mb={2.5}
                                px={2.25}
                                py={1.1}
                                borderRadius={SPACE.radiusTile}
                                sx={{ background: COLORS.greenLight, border: "1px solid #ABEFC6", width: "fit-content" }}
                            >
                                <CheckCircle sx={{ fontSize: "16px !important", color: COLORS.green }} />
                                <MDTypography variant="caption" fontWeight="medium" sx={{ color: "#05603A" }}>
                                    {getFinanceLanguage() === "tr"
                                        ? `Tüm tutarlar ${selectedCurrency} · kur ${
                                            exchangeRateUpdatedAt
                                                ? new Date(exchangeRateUpdatedAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
                                                : "—"
                                        } itibarıyla senkronize edildi`
                                        : `All amounts in ${selectedCurrency} · rates synced as of ${
                                            exchangeRateUpdatedAt
                                                ? new Date(exchangeRateUpdatedAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
                                                : "—"
                                        }`}
                                </MDTypography>
                            </MDBox>
                        )}

                        {/* Hero: the one number that answers "how are we doing" at a
                            glance, next to a preview of what needs attention right
                            now — instead of 9 equally-weighted cards competing for
                            the eye. Revenue/Cost/Returns move here as supporting
                            context for the profit figure rather than standing alone.
                            Built from the shared FinanceUiKit so every finance page
                            renders this pattern identically. */}

                        {/* Secondary metrics: real supporting numbers, deliberately
                            smaller and quieter than the hero above. */}
                        <Grid container spacing={SPACE.rowGap} mt={0.5}>
                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <FinanceKpiTile
                                    title={financeText("Receivables")}
                                    value={formatCompact(dashboardStats.receivables, selectedCurrency)}
                                    subtitle={`${dashboardStats.overdueReceivables} ${getFinanceLanguage() === "tr" ? "vadesi geçmiş hesap" : "overdue accounts"}`}
                                    icon={<ReceiptLong />}
                                    tone="amber"
                                    onClick={() => navigate("/invoice/finance/receivables")}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <FinanceKpiTile
                                    title={financeText("Payables")}
                                    value={formatCompact(dashboardStats.payables, selectedCurrency)}
                                    subtitle={getFinanceLanguage() === "tr" ? "Bekleyen tedarikçi yükümlülükleri" : "Outstanding supplier exposure"}
                                    icon={<Payments />}
                                    tone="purple"
                                    onClick={() => navigate("/invoice/finance/payables")}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <FinanceKpiTile
                                    title={financeText("Collected")}
                                    value={formatCompact(dashboardStats.collectedAmount, selectedCurrency)}
                                    subtitle={`${getFinanceLanguage() === "tr" ? "Tahsilat oranı" : "Collection rate"} ${formatPercent(dashboardStats.collectionRate)}`}
                                    icon={<CreditScore />}
                                    tone="green"
                                    onClick={() => navigate("/invoice/finance/collections")}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <FinanceKpiTile
                                    title={financeText("Cash Flow")}
                                    value={formatCompact(dashboardStats.cashFlow, selectedCurrency)}
                                    subtitle={getFinanceLanguage() === "tr" ? "Tahsil edilen tutar eksi borçlar" : "Collected amount less payables"}
                                    icon={<CurrencyExchange />}
                                    tone={dashboardStats.cashFlow < 0 ? "red" : "navy"}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <FinanceKpiTile
                                    title={financeText("Open Finance Jobs")}
                                    value={formatNumber(dashboardStats.openJobs)}
                                    subtitle={getFinanceLanguage() === "tr" ? "Aktif sevkiyat finans kayıtları" : "Active shipment finance records"}
                                    icon={<BusinessCenter />}
                                    tone="navy"
                                    onClick={() => navigate("/invoice/finance/jobs")}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={2}>
                                <FinanceKpiTile
                                    title={financeText("Invoices not sent to EDM")}
                                    value={formatNumber(edmPending.count)}
                                    subtitle={`${formatCompact(edmPending.amount, selectedCurrency)} · ${financeText("issued, waiting to be sent")}`}
                                    icon={<ScheduleSend />}
                                    tone={edmPending.count > 0 ? "amber" : "green"}
                                    onClick={() => navigate("/finance/invoices?edm=notSent")}
                                />
                            </Grid>
                        </Grid>




                        {/* The old "Finance Action Center" card here was an exact
                            duplicate of the "Needs attention" panel already shown
                            next to the hero above (same financeActionItems data,
                            second Card) — removed rather than kept as a second
                            copy of the same list. */}




                        <Grid container spacing={2.5} mt={0.25}>
                            <Grid item xs={12} xl={8}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox
                                        p={3}
                                        sx={{
                                            width: "100%",
                                        }}
                                    >
                                        <SectionHeader
                                            title={financeText("Financial Performance")}
                                            subtitle={`${financeText("Revenue, cost and collection trend")} · ${period}`}
                                            action={
                                                <Stack direction="row" spacing={2}>
                                                    {[
                                                        ["Revenue", COLORS.blue],
                                                        ["Cost", COLORS.red],
                                                        ["Collected", COLORS.green],
                                                    ].map(([label, color]) => (
                                                        <MDBox
                                                            key={label}
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={0.6}
                                                        >
                                                            <MDBox
                                                                width={9}
                                                                height={9}
                                                                borderRadius="50%"
                                                                sx={{ background: color }}
                                                            />
                                                            <MDTypography
                                                                variant="caption"
                                                                sx={{ color: COLORS.subtext }}
                                                            >
                                                                {financeText(label)}
                                                            </MDTypography>
                                                        </MDBox>
                                                    ))}
                                                </Stack>
                                            }
                                        />

                                        <LineChart
                                            data={monthlySeries}
                                            series={[
                                                {
                                                    key: "revenue",
                                                    label: "Revenue",
                                                    color: COLORS.blue,
                                                },
                                                { key: "cost", label: "Cost", color: COLORS.red },
                                                {
                                                    key: "collection",
                                                    label: "Collected",
                                                    color: COLORS.green,
                                                },
                                            ]}
                                        />
                                    </MDBox>
                                </Card>
                            </Grid>

                            <Grid item xs={12} xl={4}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <SectionHeader
                                            title={financeText("Finance Health")}
                                            subtitle={financeText("Liquidity, collection and risk adjusted score")}
                                        />

                                        <DonutChart
                                            value={dashboardStats.financeHealth}
                                            label={financeText("Health Score")}
                                            helper={financeText("out of 100")}
                                            color={
                                                dashboardStats.financeHealth >= 70
                                                    ? COLORS.green
                                                    : dashboardStats.financeHealth >= 50
                                                        ? COLORS.amber
                                                        : COLORS.red
                                            }
                                        />

                                        <Divider sx={{ my: 2.5 }} />

                                        <Grid container spacing={1.5}>
                                            <Grid item xs={6}>
                                                <MDBox
                                                    p={1.5}
                                                    borderRadius="8px"
                                                    sx={{ background: COLORS.greenLight }}
                                                >
                                                    <MDTypography
                                                        variant="caption"
                                                        fontWeight="bold"
                                                        sx={{ color: COLORS.green }}
                                                    >
                                                        {financeText("COLLECTION RATE")}
                                                    </MDTypography>
                                                    <MDTypography
                                                        variant="h5"
                                                        fontWeight="bold"
                                                        sx={{ color: COLORS.text }}
                                                    >
                                                        {formatPercent(dashboardStats.collectionRate)}
                                                    </MDTypography>
                                                </MDBox>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <MDBox
                                                    p={1.5}
                                                    borderRadius="8px"
                                                    sx={{ background: COLORS.blueLight }}
                                                >
                                                    <MDTypography
                                                        variant="caption"
                                                        fontWeight="bold"
                                                        sx={{ color: COLORS.blue }}
                                                    >
                                                        {financeText("PAYABLE COVER")}
                                                    </MDTypography>
                                                    <MDTypography
                                                        variant="h5"
                                                        fontWeight="bold"
                                                        sx={{ color: COLORS.text }}
                                                    >
                                                        {formatPercent(dashboardStats.payableCoverage)}
                                                    </MDTypography>
                                                </MDBox>
                                            </Grid>
                                        </Grid>

                                        <MDBox mt={2}>
                                            <Chip
                                                icon={
                                                    <WarningAmber
                                                        sx={{ color: `${COLORS.red} !important` }}
                                                    />
                                                }
                                                label={`${dashboardStats.overdueReceivables} overdue receivables`}
                                                onClick={() => navigate("/invoice/finance/receivables")}
                                                sx={{
                                                    width: "100%",
                                                    justifyContent: "flex-start",
                                                    background: COLORS.redLight,
                                                    color: COLORS.red,
                                                    fontWeight: 700,
                                                    cursor: "pointer",
                                                }}
                                            />
                                        </MDBox>
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>


                        <Card sx={{ ...cardSx, mt: 2.5 }}>
                            <MDBox p={3}>
                                <SectionHeader
                                    title={financeText("Recent Finance Jobs")}
                                    subtitle={financeText("Latest net invoice, return, cost, profitability and collection movements")}
                                    action={
                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            onClick={() => navigate("/invoice/finance/jobs")}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            {financeText("View All Jobs")}
                                        </MDButton>
                                    }
                                />

                                <MDBox
                                    sx={{
                                        "& .MuiTableContainer-root": {
                                            border: `1px solid ${COLORS.border}`,
                                            borderRadius: "10px",
                                            overflowX: "auto",
                                            boxShadow: "none",
                                        },
                                        "& table": {
                                            minWidth: 1520,
                                            borderCollapse: "separate",
                                            borderSpacing: 0,
                                        },
                                        "& table thead": {
                                            background:
                                                "#F8FAFC",
                                        },
                                        "& table thead th": {
                                            background: "transparent",
                                            color: "#475467 !important",
                                            fontSize: "10px !important",
                                            fontWeight: "800 !important",
                                            letterSpacing: ".085em",
                                            paddingTop: "15px !important",
                                            paddingBottom: "15px !important",
                                            borderBottom: `1px solid ${COLORS.border} !important`,
                                        },
                                        "& table tbody td": {
                                            paddingTop: "14px !important",
                                            paddingBottom: "14px !important",
                                            borderBottom: `1px solid #F0F2F5 !important`,
                                            verticalAlign: "middle",
                                        },
                                        "& table tbody tr": {
                                            background: "#FFFFFF",
                                            transition: "background .18s ease, box-shadow .18s ease",
                                        },
                                        "& table tbody tr:nth-of-type(even)": {
                                            background: "#FCFCFD",
                                        },
                                        "& table tbody tr:hover": {
                                            background: "#F5F8FF !important",
                                            boxShadow: "inset 3px 0 0 #175CD3",
                                        },
                                        "& table tbody tr:last-child td": {
                                            borderBottom: "0 !important",
                                        },
                                    }}
                                >
                                    <DataTable
                                        table={tableData}
                                        entriesPerPage={false}
                                        showTotalEntries={false}
                                        isSorted={false}
                                        noEndBorder
                                    />
                                </MDBox>
                            </MDBox>
                        </Card>

                        {/* İkincil grafik ve kırılımlar: varsayılan kapalı, üstteki filtrelere bağlı */}
                        <FinanceDetailsSection
                            storageKey="dashboard"
                            title={financeText("Detailed analysis")}
                            subtitle={financeText("Charts and breakdowns for the current filters")}
                        >
                            <Grid container spacing={SPACE.rowGap}>
                            <Grid item xs={12} lg={7}>
                                <FinanceHeroStat
                                    label={getFinanceLanguage() === "tr" ? "Net kâr — bu dönem" : "Net profit — this period"}
                                    value={formatCompact(dashboardStats.totalProfit, selectedCurrency)}
                                    tone={dashboardStats.totalProfit >= 0 ? "green" : "red"}
                                    icon={<AccountBalanceWallet />}
                                    meta={
                                        <>
                                            {getFinanceLanguage() === "tr" ? "Marj" : "Margin"} {formatPercent(dashboardStats.margin)}
                                            {" · "}
                                            {getFinanceLanguage() === "tr" ? "kesilen iadeler sonrası" : "after issued returns"}
                                        </>
                                    }
                                    context={[
                                        { label: financeText(dashboardStats.totalVatAmount > 0 ? "Revenue (incl. VAT)" : "Revenue"), value: formatCompact(dashboardStats.billedRevenue, selectedCurrency) },
                                        { label: getFinanceLanguage() === "tr" ? "Maliyet" : "Cost", value: formatCompact(dashboardStats.totalCost, selectedCurrency) },
                                        { label: getFinanceLanguage() === "tr" ? "İadeler" : "Returns issued", value: formatCompact(dashboardStats.returnedAmount, selectedCurrency) },
                                    ]}
                                >
                                    {heroProfitTrend.length > 1 && (
                                        <FinanceTrendChart
                                            months={heroProfitTrend.map((m) => ({ key: m.key, label: m.label, value: m.profit }))}
                                            formatValue={(v) => formatCompact(v, selectedCurrency)}
                                        />
                                    )}
                                </FinanceHeroStat>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <FinanceCard>
                                    <MDBox p={SPACE.cardPaddingLg} display="flex" flexDirection="column" height="100%">
                                        <FinanceSectionHeader
                                            title={getFinanceLanguage() === "tr" ? "Dikkat gerektiren" : "Needs attention"}
                                            action={
                                                financeActionItems.some((item) => item.count > 0) && (
                                                    <Chip
                                                        size="small"
                                                        label={financeActionItems.reduce((sum, item) => sum + item.count, 0)}
                                                        sx={{ background: COLORS.redLight, color: COLORS.red, fontWeight: 800 }}
                                                    />
                                                )
                                            }
                                        />
                                        <Stack spacing={1.25} sx={{ flexGrow: 1 }}>
                                            {financeActionItems
                                                .filter((item) => item.count > 0)
                                                .map((item) => (
                                                    <ButtonBase
                                                        key={item.title}
                                                        onClick={() =>
                                                            item.path ? navigate(item.path) : setProfitDrilldown(item.drilldown)
                                                        }
                                                        sx={{ width: "100%", display: "block", textAlign: "left", borderRadius: SPACE.radiusTile }}
                                                    >
                                                        <MDBox
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="space-between"
                                                            px={1.75}
                                                            py={1.25}
                                                            borderRadius={SPACE.radiusTile}
                                                            sx={{ background: COLORS.muted, "&:hover": { background: "#F2F4F7" } }}
                                                        >
                                                            <MDBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
                                                                <MDBox
                                                                    width={8}
                                                                    height={8}
                                                                    borderRadius="50%"
                                                                    flexShrink={0}
                                                                    sx={{ background: item.color }}
                                                                />
                                                                <MDBox minWidth={0}>
                                                                    <MDTypography
                                                                        variant="button"
                                                                        fontWeight="medium"
                                                                        sx={{ color: COLORS.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}
                                                                    >
                                                                        {financeText(item.title)}
                                                                    </MDTypography>
                                                                    <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                                                        {item.count} {financeText("records")}
                                                                    </MDTypography>
                                                                </MDBox>
                                                            </MDBox>
                                                            <MDBox display="flex" alignItems="center" gap={0.75} flexShrink={0}>
                                                                {item.amount !== null && (
                                                                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: item.color }}>
                                                                        {formatCompact(item.amount, selectedCurrency)}
                                                                    </MDTypography>
                                                                )}
                                                                <ArrowForward sx={{ fontSize: "13px !important", color: COLORS.subtext }} />
                                                            </MDBox>
                                                        </MDBox>
                                                    </ButtonBase>
                                                ))}
                                            {financeActionItems.every((item) => item.count === 0) && (
                                                <MDBox display="flex" alignItems="center" justifyContent="center" flexGrow={1} py={3}>
                                                    <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                                        {getFinanceLanguage() === "tr" ? "Şu anda bekleyen bir öğe yok." : "Nothing needs attention right now."}
                                                    </MDTypography>
                                                </MDBox>
                                            )}
                                        </Stack>
                                    </MDBox>
                                </FinanceCard>
                            </Grid>
                        </Grid>

                            <Card sx={{ ...cardSx, mt: 3 }}>
                            <MDBox p={3}>
                                <SectionHeader
                                    title={financeText("Revenue / Expense Report Filters")}
                                    subtitle={financeText("Select a date, company, geography, transport mode, return status, credit risk and report currency")}
                                    action={
                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            onClick={() => {
                                                setReportStartDate(`${currentYear}-01-01`);
                                                setReportEndDate(todayValue);
                                                setSelectedParty(null);
                                                setSelectedTransportMode("ALL");
                                                setSelectedInvoiceOrigin("ALL");
                                                setSelectedReturnStatus("ALL");
                                                setSelectedCollectionRisk("ALL");
                                                setSelectedGeographicScope("ALL");
                                                setSelectedCurrency("EUR");
                                            }}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            {financeText("Clear Filters")}
                                        </MDButton>
                                    }
                                />

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} lg={2}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="date"
                                            label={financeText("Start Date")}
                                            value={reportStartDate}
                                            onChange={(event) =>
                                                setReportStartDate(event.target.value)
                                            }
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={2}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="date"
                                            label={financeText("End Date")}
                                            value={reportEndDate}
                                            onChange={(event) => setReportEndDate(event.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={2}>
                                        <Autocomplete
                                            size="small"
                                            options={partyOptions}
                                            value={selectedParty}
                                            onChange={(event, newValue) => setSelectedParty(newValue)}
                                            getOptionLabel={(option) => option?.label || ""}
                                            isOptionEqualToValue={(option, value) =>
                                                option.type === value.type && option.name === value.name
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={financeText("Customer / Supplier")}
                                                    placeholder={financeText("Select a company")}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={2}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Geographic Scope")}
                                            value={selectedGeographicScope}
                                            onChange={(event) =>
                                                setSelectedGeographicScope(event.target.value)
                                            }
                                        >
                                            <MenuItem value="ALL">{financeText("All Geographic Scopes")}</MenuItem>
                                            <MenuItem value="DOMESTIC">{financeText("Domestic")}</MenuItem>
                                            <MenuItem value="INTERNATIONAL">{financeText("International")}</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={2}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Transport Mode")}
                                            value={selectedTransportMode}
                                            onChange={(event) =>
                                                setSelectedTransportMode(event.target.value)
                                            }
                                        >
                                            <MenuItem value="ALL">{financeText("All Transport Modes")}</MenuItem>
                                            {transportModeOptions.map((mode) => (
                                                <MenuItem key={mode} value={mode}>
                                                    {financeText(mode)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={2}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Currency")}
                                            value={selectedCurrency}
                                            onChange={(event) =>
                                                setSelectedCurrency(event.target.value)
                                            }
                                        >
                                            <MenuItem value="EUR">EUR</MenuItem>
                                            <MenuItem value="USD">USD</MenuItem>
                                            <MenuItem value="GBP">GBP</MenuItem>
                                            <MenuItem value="TRY">TRY</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={2}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Invoice Origin")}
                                            value={selectedInvoiceOrigin}
                                            onChange={(event) =>
                                                setSelectedInvoiceOrigin(event.target.value)
                                            }
                                        >
                                            <MenuItem value="ALL">{financeText("All Origins")}</MenuItem>
                                            <MenuItem value="TMS">TMS</MenuItem>
                                            <MenuItem value="MANUAL">{financeText("Manual")}</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={2}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Return Status")}
                                            value={selectedReturnStatus}
                                            onChange={(event) =>
                                                setSelectedReturnStatus(event.target.value)
                                            }
                                        >
                                            {returnStatusOptions.map((status) => (
                                                <MenuItem key={status} value={status}>
                                                    {status === "ALL" ? financeText("All Return Statuses") : financeText(status)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6} lg={2}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Credit Risk")}
                                            value={selectedCollectionRisk}
                                            onChange={(event) =>
                                                setSelectedCollectionRisk(event.target.value)
                                            }
                                        >
                                            {COLLECTION_RISK_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {financeText(option.label)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </Grid>
                            </MDBox>
                        </Card>

                            <Card sx={{ ...cardSx, mt: 2.5 }}>
                            <MDBox p={3}>
                                <SectionHeader
                                    title={financeText("Collection Risk Monitor")}
                                    subtitle={financeText("Highest outstanding exposure among doubtful, bad debt and written-off invoices")}
                                    action={
                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            onClick={() => navigate("/invoice/finance/collections")}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            {financeText("Manage Risks")}
                                        </MDButton>
                                    }
                                />

                                <MDBox display="flex" flexWrap="wrap" gap={1} mb={2}>
                                    {collectionRiskSummary.distribution.map((item) => {
                                        const style = COLLECTION_RISK_STYLES[item.value];
                                        return (
                                            <Chip
                                                key={item.value}
                                                clickable
                                                onClick={() => setSelectedCollectionRisk(item.value)}
                                                label={`${financeText(item.label)}: ${item.count}`}
                                                sx={{
                                                    color: style.color,
                                                    background: style.background,
                                                    border: `1px solid ${style.border}`,
                                                    fontWeight: 800,
                                                }}
                                            />
                                        );
                                    })}
                                </MDBox>

                                <Stack spacing={1}>
                                    {collectionRiskSummary.riskyJobs
                                        .slice(0, 8)
                                        .map((job, index) => (
                                            <ButtonBase
                                                key={job?._id || job?.id || `${getJobNo(job)}-${index}`}
                                                onClick={() =>
                                                    navigate("/invoice/finance/collections", {
                                                        state: {
                                                            invoiceId:
                                                                job?.invoiceId || job?.invoice_id || job?._id,
                                                            riskStatus: getCollectionRisk(job).status,
                                                        },
                                                    })
                                                }
                                                sx={{
                                                    display: "block",
                                                    width: "100%",
                                                    textAlign: "left",
                                                    borderRadius: "8px",
                                                }}
                                            >
                                                <MDBox
                                                    p={1.4}
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    gap={2}
                                                    borderRadius="8px"
                                                    sx={{
                                                        border: `1px solid ${COLORS.border}`,
                                                        background: "#FCFCFD",
                                                    }}
                                                >
                                                    <MDBox minWidth={0}>
                                                        <MDTypography
                                                            variant="button"
                                                            fontWeight="bold"
                                                            sx={{ color: COLORS.text }}
                                                        >
                                                            {getJobNo(job)} · {getCustomerName(job)}
                                                        </MDTypography>
                                                        <MDTypography
                                                            variant="caption"
                                                            display="block"
                                                            noWrap
                                                            sx={{ color: COLORS.subtext }}
                                                        >
                                                            {getCollectionRisk(job).reason ||
                                                                financeText("No risk reason entered")}
                                                        </MDTypography>
                                                    </MDBox>
                                                    <MDBox
                                                        display="flex"
                                                        alignItems="center"
                                                        gap={1.5}
                                                        flexShrink={0}
                                                    >
                                                        <MDTypography
                                                            variant="button"
                                                            fontWeight="bold"
                                                            sx={{ color: COLORS.red }}
                                                        >
                                                            {formatCompact(
                                                                getConvertedReceivable(job),
                                                                selectedCurrency,
                                                            )}
                                                        </MDTypography>
                                                        <CollectionRiskChip job={job} />
                                                        <ArrowForward sx={{ color: COLORS.blue }} />
                                                    </MDBox>
                                                </MDBox>
                                            </ButtonBase>
                                        ))}
                                    {!collectionRiskSummary.riskyJobs.length && (
                                        <MDTypography
                                            variant="body2"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {financeText("No doubtful, bad debt or written-off collection records match the selected filters.")}
                                        </MDTypography>
                                    )}
                                </Stack>
                            </MDBox>
                        </Card>

                            <MonthlyIncomeExpenseTable
                            rows={monthlyIncomeExpenseRows}
                            language={financeLanguage}
                            currency={selectedCurrency}
                            exchangeRates={exchangeRates}
                            exchangeRateUpdatedAt={exchangeRateUpdatedAt}
                            loadingRates={loadingRates}
                            onExport={exportFilteredReportToExcel}
                        />

                            <Grid container spacing={2.5} mt={0.25}>
                            <Grid item xs={12}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <SectionHeader
                                            title={financeText("90-Day Cash Flow Forecast")}
                                            subtitle={`${financeText("Expected collections and supplier payments in")} ${selectedCurrency}`}
                                        />
                                        <Grid container spacing={1.5}>
                                            {cashFlowForecast.map((item) => (
                                                <Grid item xs={12} sm={6} lg={3} key={item.label}>
                                                    <MDBox
                                                        p={1.75}
                                                        borderRadius="8px"
                                                        sx={{
                                                            background: "#F9FAFB",
                                                            border: `1px solid ${COLORS.border}`,
                                                        }}
                                                    >
                                                        <MDTypography
                                                            variant="button"
                                                            fontWeight="bold"
                                                            sx={{ color: COLORS.text }}
                                                        >
                                                            {financeText(item.label)}
                                                        </MDTypography>
                                                        <MDBox
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            mt={1}
                                                        >
                                                            <MDTypography
                                                                variant="caption"
                                                                sx={{ color: COLORS.green }}
                                                            >
                                                                In{" "}
                                                                {formatCompact(item.inflow, selectedCurrency)}
                                                            </MDTypography>
                                                            <MDTypography
                                                                variant="caption"
                                                                sx={{ color: COLORS.red }}
                                                            >
                                                                Out{" "}
                                                                {formatCompact(item.outflow, selectedCurrency)}
                                                            </MDTypography>
                                                        </MDBox>
                                                        <Divider sx={{ my: 1 }} />
                                                        <MDTypography
                                                            variant="h6"
                                                            fontWeight="bold"
                                                            sx={{
                                                                color:
                                                                    item.net >= 0 ? COLORS.green : COLORS.red,
                                                            }}
                                                        >
                                                            {formatCompact(item.net, selectedCurrency)}
                                                        </MDTypography>
                                                        <MDTypography
                                                            variant="caption"
                                                            sx={{ color: COLORS.subtext }}
                                                        >
                                                            {financeText("Forecast net cash")}
                                                        </MDTypography>
                                                    </MDBox>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>

                            <Grid container spacing={2.5} mt={0.25}>
                            <Grid item xs={12}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <SectionHeader
                                            title={financeText("Profitability Drill-Down")}
                                            subtitle={financeText("Customer-level revenue, cost and margin")}
                                            action={
                                                <Chip
                                                    icon={<Visibility />}
                                                    label={financeText("Click a customer for job details")}
                                                    size="small"
                                                    sx={{
                                                        background: COLORS.blueLight,
                                                        color: COLORS.blue,
                                                        fontWeight: 700,
                                                    }}
                                                />
                                            }
                                        />
                                        <Stack spacing={1}>
                                            {profitabilityGroups.map((item) => (
                                                <ButtonBase
                                                    key={item.name}
                                                    onClick={() =>
                                                        setProfitDrilldown({
                                                            title: `${item.name} profitability`,
                                                            jobs: item.jobs,
                                                        })
                                                    }
                                                    sx={{
                                                        display: "block",
                                                        textAlign: "left",
                                                        borderRadius: "10px",
                                                        cursor: "pointer",
                                                        transition:
                                                            "transform .16s ease, box-shadow .16s ease",
                                                        "&:hover": {
                                                            transform: "translateX(3px)",
                                                            boxShadow: "0 4px 14px rgba(15, 23, 42, 0.06)",
                                                        },
                                                    }}
                                                >
                                                    <MDBox
                                                        p={1.25}
                                                        borderRadius="10px"
                                                        sx={{
                                                            border: `1px solid ${COLORS.border}`,
                                                            background: "#FCFCFD",
                                                        }}
                                                    >
                                                        <MDBox
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            alignItems="flex-start"
                                                            gap={1}
                                                        >
                                                            <MDTypography
                                                                variant="button"
                                                                fontWeight="bold"
                                                                sx={{
                                                                    color: COLORS.text,
                                                                    whiteSpace: "normal",
                                                                    overflowWrap: "anywhere",
                                                                    lineHeight: 1.35,
                                                                    flex: 1,
                                                                }}
                                                            >
                                                                {item.name}
                                                            </MDTypography>
                                                            <MDTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                flexShrink={0}
                                                                sx={{
                                                                    color:
                                                                        item.profit >= 0
                                                                            ? COLORS.green
                                                                            : COLORS.red,
                                                                }}
                                                            >
                                                                {formatPercent(item.margin)}
                                                            </MDTypography>
                                                        </MDBox>
                                                        <MDBox
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                        >
                                                            <MDTypography
                                                                variant="caption"
                                                                sx={{ color: COLORS.subtext }}
                                                            >
                                                                {item.jobs.length} jobs · Profit{" "}
                                                                {formatCompact(item.profit, selectedCurrency)}
                                                            </MDTypography>
                                                            <MDBox
                                                                display="flex"
                                                                alignItems="center"
                                                                gap={0.4}
                                                            >
                                                                <MDTypography
                                                                    variant="caption"
                                                                    fontWeight="bold"
                                                                    sx={{ color: COLORS.blue }}
                                                                >
                                                                    {financeText("View details")}
                                                                </MDTypography>
                                                                <ArrowForward
                                                                    sx={{
                                                                        fontSize: "14px !important",
                                                                        color: COLORS.blue,
                                                                    }}
                                                                />
                                                            </MDBox>
                                                        </MDBox>
                                                    </MDBox>
                                                </ButtonBase>
                                            ))}
                                        </Stack>
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>

                            <MDBox mt={3}>
                            <SectionHeader
                                title={financeText("Transport Performance")}
                                subtitle={financeText("Road, ocean and air shipment volume, revenue, cost and margin overview")}
                                action={
                                    dominantTransportMode && (
                                        <Chip
                                            icon={
                                                <WorkspacePremium
                                                    sx={{ color: `${COLORS.blue} !important` }}
                                                />
                                            }
                                            label={`${financeText("Top mode")}: ${financeText(dominantTransportMode.mode)} · ${dominantTransportMode.jobs} ${financeText("jobs")}`}
                                            sx={{
                                                background: COLORS.blueLight,
                                                color: COLORS.blue,
                                                fontWeight: 700,
                                            }}
                                        />
                                    )
                                }
                            />

                            <Grid container spacing={2.5}>
                                {[
                                    {
                                        mode: "Ocean",
                                        icon: <DirectionsBoat />,
                                        tone: "blue",
                                        subtitle: "FCL, LCL and ocean services",
                                    },
                                    {
                                        mode: "Road",
                                        icon: <LocalShipping />,
                                        tone: "amber",
                                        subtitle: "FTL, LTL and road services",
                                    },
                                    {
                                        mode: "Air",
                                        icon: <Flight />,
                                        tone: "purple",
                                        subtitle: "Standard, express and charter",
                                    },
                                ].map(({ mode, icon, tone, subtitle }) => {
                                    const summary = transportByMode[mode] || {
                                        jobs: 0,
                                        revenue: 0,
                                        profit: 0,
                                        margin: 0,
                                    };

                                    return (
                                        <Grid item xs={12} md={4} key={mode}>
                                            <BankingMetricCard
                                                title={`${mode} Shipments`}
                                                value={formatNumber(summary.jobs)}
                                                subtitle={`${subtitle} · Revenue ${formatCompact(summary.revenue)} · Margin ${formatPercent(summary.margin)}`}
                                                icon={icon}
                                                tone={tone}
                                                delta={summary.margin}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </MDBox>

                            <Grid container spacing={2.5} mt={0.25}>
                            <Grid item xs={12} lg={7}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <SectionHeader
                                            title={financeText("Revenue by Transport Mode")}
                                            subtitle={financeText("Financial contribution of ocean, road and air operations")}
                                            action={
                                                <Chip
                                                    label={`${pageFilteredJobs.length} ${getFinanceLanguage() === "tr" ? "toplam iş" : "total jobs"}`}
                                                    size="small"
                                                    sx={{
                                                        background: "#F2F4F7",
                                                        color: COLORS.subtext,
                                                        fontWeight: 700,
                                                    }}
                                                />
                                            }
                                        />
                                        <ModeBarChart
                                            data={transportModeSummary}
                                            metric="revenue"
                                        />

                                        <Divider sx={{ my: 2.5 }} />

                                        <Grid container spacing={1.5}>
                                            {transportModeSummary.slice(0, 3).map((item) => (
                                                <Grid item xs={12} sm={4} key={item.mode}>
                                                    <MDBox
                                                        p={1.5}
                                                        borderRadius="8px"
                                                        sx={{
                                                            background: "#F9FAFB",
                                                            border: `1px solid ${COLORS.border}`,
                                                        }}
                                                    >
                                                        <MDTypography
                                                            variant="caption"
                                                            fontWeight="bold"
                                                            sx={{ color: COLORS.subtext }}
                                                        >
                                                            {financeText(item.mode).toUpperCase()} {getFinanceLanguage() === "tr" ? "KÂR" : "PROFIT"}
                                                        </MDTypography>
                                                        <MDTypography
                                                            variant="h6"
                                                            fontWeight="bold"
                                                            sx={{
                                                                color:
                                                                    item.profit >= 0 ? COLORS.green : COLORS.red,
                                                            }}
                                                        >
                                                            {formatCompact(item.profit)}
                                                        </MDTypography>
                                                        <MDTypography
                                                            variant="caption"
                                                            sx={{ color: COLORS.subtext }}
                                                        >
                                                            {formatPercent(item.margin)} {getFinanceLanguage() === "tr" ? "marj" : "margin"}
                                                        </MDTypography>
                                                    </MDBox>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </MDBox>
                                </Card>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <SectionHeader
                                            title={financeText("Shipment Type Distribution")}
                                            subtitle={financeText("Most frequently used transport and service types")}
                                            action={
                                                <Chip
                                                    icon={
                                                        <Inventory2
                                                            sx={{ color: `${COLORS.purple} !important` }}
                                                        />
                                                    }
                                                    label={`${shipmentTypeSummary.length} active types`}
                                                    size="small"
                                                    sx={{
                                                        background: COLORS.purpleLight,
                                                        color: COLORS.purple,
                                                        fontWeight: 700,
                                                    }}
                                                />
                                            }
                                        />

                                        {shipmentTypeSummary.length ? (
                                            <ShipmentTypeBars
                                                data={shipmentTypeSummary}
                                                total={pageFilteredJobs.length}
                                            />
                                        ) : (
                                            <MDBox
                                                p={3}
                                                textAlign="center"
                                                borderRadius="8px"
                                                sx={{
                                                    background: "#F9FAFB",
                                                    border: `1px dashed ${COLORS.border}`,
                                                }}
                                            >
                                                <Inventory2 sx={{ color: COLORS.subtext, mb: 1 }} />
                                                <MDTypography
                                                    variant="button"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Shipment type data not found")}
                                                </MDTypography>
                                                <MDTypography
                                                    variant="caption"
                                                    display="block"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("Add shipmentType, serviceType or transportType fields to finance jobs.")}
                                                </MDTypography>
                                            </MDBox>
                                        )}
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>

                            <Grid container spacing={2.5} mt={0.25}>
                            <Grid item xs={12} lg={5}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <SectionHeader
                                            title={financeText("Receivables Aging")}
                                            subtitle={financeText("Outstanding customer balances by overdue period")}
                                            action={
                                                <MDButton
                                                    variant="text"
                                                    color="dark"
                                                    onClick={() =>
                                                        navigate("/invoice/finance/receivables")
                                                    }
                                                >
                                                    {financeText("View details")}
                                                </MDButton>
                                            }
                                        />

                                        {agingData.map((item) => (
                                            <AgingBar
                                                key={item.label}
                                                {...item}
                                                total={dashboardStats.receivables}
                                            />
                                        ))}

                                        <MDBox
                                            mt={2.5}
                                            p={2}
                                            borderRadius="8px"
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            sx={{
                                                background: "#F9FAFB",
                                                border: `1px solid ${COLORS.border}`,
                                            }}
                                        >
                                            <MDBox>
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("Total Outstanding")}
                                                </MDTypography>
                                                <MDTypography
                                                    variant="h5"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {formatCompact(
                                                        dashboardStats.receivables,
                                                        selectedCurrency,
                                                    )}
                                                </MDTypography>
                                            </MDBox>
                                            <MDBox textAlign="right">
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("Overdue exposure")}
                                                </MDTypography>
                                                <MDTypography
                                                    variant="button"
                                                    fontWeight="bold"
                                                    display="block"
                                                    sx={{ color: COLORS.red }}
                                                >
                                                    {formatCompact(
                                                        dashboardStats.overdueAmount,
                                                        selectedCurrency,
                                                    )}
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>
                                    </MDBox>
                                </Card>
                            </Grid>

                            <Grid item xs={12} lg={3}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <SectionHeader
                                            title={financeText("Top Customers")}
                                            subtitle={financeText("Revenue concentration by account")}
                                        />

                                        {topCustomers.length ? (
                                            topCustomers.map((customer, index) => {
                                                const share = dashboardStats.totalRevenue
                                                    ? (customer.amount / dashboardStats.totalRevenue) *
                                                    100
                                                    : 0;

                                                return (
                                                    <MDBox key={customer.name} mb={2}>
                                                        <MDBox
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="space-between"
                                                            gap={1}
                                                            mb={0.7}
                                                        >
                                                            <MDBox
                                                                display="flex"
                                                                alignItems="center"
                                                                gap={1}
                                                                minWidth={0}
                                                            >
                                                                <Avatar
                                                                    sx={{
                                                                        width: 30,
                                                                        height: 30,
                                                                        bgcolor:
                                                                            index < 3 ? COLORS.navy : "#EEF2F6",
                                                                        color: index < 3 ? "#FFFFFF" : COLORS.navy,
                                                                        fontSize: "11px",
                                                                        fontWeight: 700,
                                                                    }}
                                                                >
                                                                    {index + 1}
                                                                </Avatar>
                                                                <MDTypography
                                                                    variant="button"
                                                                    fontWeight="bold"
                                                                    noWrap
                                                                    sx={{ color: COLORS.text, maxWidth: 135 }}
                                                                >
                                                                    {customer.name}
                                                                </MDTypography>
                                                            </MDBox>
                                                            <MDTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                sx={{ color: COLORS.subtext }}
                                                            >
                                                                {share.toFixed(1)}%
                                                            </MDTypography>
                                                        </MDBox>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={Math.min(share, 100)}
                                                            sx={{
                                                                height: 6,
                                                                borderRadius: "999px",
                                                                backgroundColor: "#F2F4F7",
                                                                "& .MuiLinearProgress-bar": {
                                                                    borderRadius: "999px",
                                                                    background:
                                                                        index === 0 ? COLORS.blue : COLORS.navySoft,
                                                                },
                                                            }}
                                                        />
                                                    </MDBox>
                                                );
                                            })
                                        ) : (
                                            <MDTypography
                                                variant="caption"
                                                sx={{ color: COLORS.subtext }}
                                            >
                                                {financeText("No customer revenue data available.")}
                                            </MDTypography>
                                        )}
                                    </MDBox>
                                </Card>
                            </Grid>

                            <Grid item xs={12} lg={4}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <SectionHeader
                                            title={financeText("Finance Operations")}
                                            subtitle={financeText("Frequently used finance workflows")}
                                        />

                                        <Stack spacing={1.15}>
                                            <QuickAction
                                                icon={<Add fontSize="small" />}
                                                title={financeText("Create Customer Invoice")}
                                                subtitle={financeText("Generate a new shipment invoice")}
                                                tone="blue"
                                                onClick={() =>
                                                    navigate("/invoice/finance/manual-invoices/new")
                                                }
                                            />
                                            <QuickAction
                                                icon={<Payments fontSize="small" />}
                                                title={financeText("Record Collection")}
                                                subtitle={financeText("Register incoming customer payment")}
                                                tone="green"
                                                onClick={() => navigate("/invoice/finance/collections")}
                                            />
                                            <QuickAction
                                                icon={<TrendingDown fontSize="small" />}
                                                title={financeText("Add Supplier Cost")}
                                                subtitle={financeText("Book operational or supplier expense")}
                                                tone="red"
                                                onClick={() =>
                                                    navigate("/invoice/finance/supplier-costs")
                                                }
                                            />
                                            <QuickAction
                                                icon={<FileUpload fontSize="small" />}
                                                title={financeText("Upload Supplier Invoice")}
                                                subtitle={financeText("Attach invoice and supporting document")}
                                                tone="amber"
                                                onClick={() =>
                                                    navigate("/invoice/finance/supplier-invoices")
                                                }
                                            />
                                        </Stack>

                                        <MDBox
                                            mt={2}
                                            p={1.75}
                                            borderRadius="8px"
                                            sx={{
                                                background:
                                                    "#EEF4FF",
                                                border: "1px solid #D1E0FF",
                                            }}
                                        >
                                            <MDBox display="flex" alignItems="center" gap={1.2}>
                                                <AccountBalance sx={{ color: COLORS.blue }} />
                                                <MDBox>
                                                    <MDTypography
                                                        variant="button"
                                                        fontWeight="bold"
                                                        sx={{ color: COLORS.text }}
                                                    >
                                                        {financeText("Bank Reconciliation")}
                                                    </MDTypography>
                                                    <MDTypography
                                                        variant="caption"
                                                        display="block"
                                                        sx={{ color: COLORS.subtext }}
                                                    >
                                                        {financeText("Review unmatched finance movements.")}
                                                    </MDTypography>
                                                </MDBox>
                                            </MDBox>
                                        </MDBox>
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>
                        </FinanceDetailsSection>

                    </>
                )}
            </MDBox>

            <Dialog
                open={Boolean(profitDrilldown)}
                onClose={() => setProfitDrilldown(null)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ fontWeight: 800, color: COLORS.navy }}>
                    {profitDrilldown?.title ? financeText(profitDrilldown.title) : financeText("Finance Details")}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={1.25}>
                        {(profitDrilldown?.jobs || []).map((job, index) => {
                            const revenue = getConvertedRevenue(job);
                            const cost = convertCostAmount(job, getCost(job));
                            return (
                                <MDBox
                                    key={job?._id || job?.id || `${getJobNo(job)}-${index}`}
                                    p={1.6}
                                    borderRadius="8px"
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap={2}
                                    sx={{
                                        background: "#F9FAFB",
                                        border: `1px solid ${COLORS.border}`,
                                    }}
                                >
                                    <MDBox minWidth={0}>
                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.text }}
                                        >
                                            {getJobNo(job)}
                                        </MDTypography>
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            noWrap
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {getCustomerName(job)} · {getSupplierName(job)} ·{" "}
                                            {getTransportMode(job)}
                                        </MDTypography>
                                    </MDBox>
                                    <MDBox textAlign="right" flexShrink={0}>
                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            sx={{
                                                color: revenue - cost >= 0 ? COLORS.green : COLORS.red,
                                            }}
                                        >
                                            {formatCompact(revenue - cost, selectedCurrency)}
                                        </MDTypography>
                                        <MDTypography
                                            variant="caption"
                                            display="block"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            Revenue {formatCompact(revenue, selectedCurrency)} · Cost{" "}
                                            {formatCompact(cost, selectedCurrency)}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>
                            );
                        })}
                        {!profitDrilldown?.jobs?.length && (
                            <MDTypography variant="body2" sx={{ color: COLORS.subtext }}>
                                {financeText("No matching records.")}
                            </MDTypography>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <MDButton
                        variant="outlined"
                        color="dark"
                        onClick={() => setProfitDrilldown(null)}
                    >
                        {financeText("Close")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
};

export default FinanceDashboard;